/**
 * assets/diamond-search-education.js
 *
 * Stage 5C17 education system for Diamond Search, extended in Stage 7A2
 * with a shared interactive module mount (Cut scale is the first module).
 *
 * Extracted from sections/diamond-search.liquid to stay under Shopify's
 * 256 KB section template limit.
 *
 * Preserved: one content map, one #diamondSearchEdu shell, desktop
 * popover, mobile modal, click-outside, Escape, focus restore, trigger
 * aria-expanded, certificate trigger isolation, no filter/API coupling.
 *
 * Stage 7A2: scale-type registry entries render inside
 * #diamondSearchEduModule. Education state is local to this file and
 * never reads or writes Cut filter controls (#cutMin, #cutMax,
 * #cutRangeMin, #cutRangeMax, #cutOptions) or diamond-api.js.
 *
 * Stage 7A3C.2: Cut scale visuals resolve from #diamondSearchEduModule
 * data-edu-cut-* attributes (Shopify asset_url). Local education state
 * swaps the displayed PNG only; it does not touch the Cut filter.
 *
 * Stage 7A3C.3: final Cut intro + state copy. Scale still swaps only
 * the local visual, grade caption, and explanation.
 *
 * Stage 7A3C.4: desktop scale modules are viewport-centered in CSS
 * (~800px × 88vh). positionPanel must not write inline left/top/width
 * for those panels, or it would re-anchor them to the filter trigger.
 *
 * Stage 7A3C.5: state copy is an Insight Card. The opening sentence is
 * wrapped in .diamond-search__edu-scale-explain-lead (semibold). Wording,
 * PNG mapping, and Cut filter isolation are unchanged.
 *
 * Cut AEC selector: the five-circle radio control is replaced with the
 * approved discrete Ivanov slider (Fair / Good / Very Good / Excellent /
 * Ideal). Visuals, captions, explanation copy, and PNG mapping stay on
 * applyScaleState.
 *
 * Clarity AEC selector: the SI2–FL circular radio control is replaced with
 * the approved discrete Ivanov slider (SI2 → FL, more included to flawless).
 * Visuals, 10× magnifier, captions, full names, explanation copy, and
 * asset mapping stay on applyClarityState. The 10× comparison strip stays
 * exclusive to inline 04 CLARITY.
 *
 * Color AEC selector: the D–K circular radio control is replaced with the
 * approved discrete Ivanov slider (D → K, left to right). Visuals, captions,
 * category, explanation copy, and WebP mapping stay on applyColorState.
 * The D–K comparison strip stays exclusive to inline 03 COLOR.
 *
 * Color AEC: color.variant === "color" renders a Color-specific layout
 * inside the shared --module shell. Cut's renderScaleModule / applyScaleState
 * and PNG mapping are unchanged. Color education state is local and never
 * reads or writes Color filter controls (#colorMin, #colorMax,
 * #colorRangeMin, #colorRangeMax, #colorOptions) or diamond-api.js.
 *
 * Color AEC visuals resolve from #diamondSearchEduModule data-edu-color-*
 * attributes (Shopify asset_url). All eight grades share one master
 * photorealistic diamond; only body color changes. Layout constraints
 * stay on the Color stage (do not change Cut).
 *
 * Clarity AEC: clarity.variant === "clarity" renders a Clarity-specific
 * layout inside the shared --module shell. Cut and Color render/apply
 * paths are unchanged. Clarity education state is local and never reads
 * or writes Clarity filter controls (#clarityMin, #clarityMax,
 * #clarityRangeMin, #clarityRangeMax, #clarityOptions) or diamond-api.js.
 *
 * Clarity visuals: dedicated per-grade master + matching 10× inspection
 * state (data-edu-clarity-{id} / data-edu-clarity-{id}-10x). Each 10×
 * state uses the approved educational inspection visual for that grade
 * (SI2 → FL), sharing the same light diamond tone, scale, and facet
 * texture. SI2/SI1 have dedicated masters; VS1–FL use the clean master.
 * Geometry, camera, and inspection point stay locked. Loupe size remains
 * 128px / 104px mobile.
 *
 * Carat AEC: carat.variant === "carat" renders a Carat-specific layout
 * inside the shared --module shell. Cut, Color, and Clarity render/apply
 * paths are unchanged. Carat education state is local and never reads
 * or writes Carat filter controls (#caratMin, #caratMax, dual-range
 * inputs) or diamond-api.js / Nivoda query logic.
 *
 * Carat visuals: one static Size 5.5 hand (data-edu-carat-hand) plus a
 * separate shape overlay. Round uses data-edu-carat-diamond (typical
 * diameter). Oval uses data-edu-carat-diamond-oval (typical L×W, vertical).
 * Pear uses data-edu-carat-diamond-pear (typical L×W, vertical, point down).
 * Marquise uses data-edu-carat-diamond-marquise (typical L×W, vertical,
 * point up + point down). Cushion uses data-edu-carat-diamond-cushion
 * (typical L×W, vertical, near-square). Princess uses
 * data-edu-carat-diamond-princess (typical L×W, vertical, sharp square).
 * Emerald uses data-edu-carat-diamond-emerald (typical L×W, vertical,
 * clipped-corner rectangle). Radiant uses data-edu-carat-diamond-radiant
 * (typical L×W, vertical, brilliant/mixed-cut clipped-corner rectangle).
 * Heart uses data-edu-carat-diamond-heart (typical L×W, vertical, cleft
 * up, two lobes up, point down). Asscher uses
 * data-edu-carat-diamond-asscher (typical L×W, vertical, near-square
 * octagonal step-cut).
 * The Search shape filter selects which model to show. Slider carat maps
 * through IvanovCaratAec. Displayed
 * px/mm is the native constant scaled by the rendered hand width. The
 * approved hand master and diamond
 * anchor stay fixed for every shape. No millimeter ruler. Skin Tone is
 * five same-canvas states (1 lightest → 5 darkest), default 1 lightest.
 * Only the hand image crossfades; carat, overlay size, and diamond
 * anchor stay fixed.
 *
 * shopify:section:load tears down the previous instance before re-init
 * so theme-editor reloads do not stack document/window listeners.
 *
 * DETAIL-7E.2B: Shared Search/Detail controller. Initialization depends on
 * #diamondSearchEdu existing, not #diamondSearchLayout. Document-level
 * delegated .diamond-search__edu-btn[data-edu-key] discovery covers Detail
 * buttons hydrated after API fetch. Search-only sidebar listeners stay gated
 * behind #diamondSearchLayout. openEdu() reads data-edu-state /
 * data-edu-carat / data-edu-shape from the originating trigger.
 */
(function () {
  "use strict";

  var eduTeardown = null;

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function initEducation() {
    var EDU_CONTENT = {
      carat: {
        type: "scale",
        variant: "carat",
        title: "Diamond Carat",
        intro: "Carat measures a diamond's weight. One carat equals 200 milligrams, but carat weight does not directly determine how large a diamond appears.",
        body: "Carat measures a diamond's weight. One carat equals 200 milligrams, but carat weight does not directly determine how large a diamond appears.",
        defaultState: "100",
        explanation: "Carat measures weight, not size. Two diamonds with the same carat weight can appear different in size depending on their cut and proportions.",
        varyNote: "Actual dimensions vary by diamond.",
        states: [
          { id: "025", label: "0.25 CT", carat: 0.25 },
          { id: "050", label: "0.50 CT", carat: 0.5 },
          { id: "100", label: "1.00 CT", carat: 1 },
          { id: "150", label: "1.50 CT", carat: 1.5 },
          { id: "200", label: "2.00 CT", carat: 2 },
          { id: "300", label: "3.00 CT", carat: 3 },
          { id: "500", label: "5.00 CT", carat: 5 },
          { id: "700", label: "7.00 CT", carat: 7 },
          { id: "1000", label: "10.00 CT", carat: 10 }
        ]
      },
      cut: {
        type: "scale",
        title: "Cut",
        intro: "Cut describes how a diamond's proportions, angles, and facet alignment influence the way light travels through the stone. A well-cut diamond returns more light to the eye, creating greater brilliance, fire, and sparkle.",
        body: "Cut describes how a diamond's proportions, angles, and facet alignment influence the way light travels through the stone. A well-cut diamond returns more light to the eye, creating greater brilliance, fire, and sparkle.",
        defaultState: "excellent",
        states: [
          {
            id: "fair",
            label: "Fair",
            visual: "edu-cut-fair.png",
            explanation: "Reduced light performance. More light escapes through the pavilion and sides instead of returning to the eye, resulting in noticeably lower brilliance, fire, and sparkle."
          },
          {
            id: "good",
            label: "Good",
            visual: "edu-cut-good.png",
            explanation: "Balanced light performance. A meaningful amount of light returns through the top, while more escapes through the pavilion and sides. The diamond can still appear bright, but with less brilliance and sparkle than higher cut grades."
          },
          {
            id: "very-good",
            label: "Very Good",
            visual: "edu-cut-very-good.png",
            explanation: "Strong light performance. Most light is returned through the top of the diamond, while a small amount escapes through the pavilion. The result is excellent brilliance with slightly more light leakage than higher cut grades."
          },
          {
            id: "excellent",
            label: "Excellent",
            visual: "edu-cut-excellent.png",
            explanation: "Outstanding light performance. Excellent proportions return most light through the top of the diamond, producing exceptional brilliance and sparkle with only minimal light leakage."
          },
          {
            id: "ideal",
            label: "Ideal",
            visual: "edu-cut-ideal.png",
            explanation: "Exceptional light performance. Precisely balanced proportions return nearly all available light through the top of the diamond, creating maximum brilliance, fire, and sparkle with minimal light leakage."
          }
        ]
      },
      fluorescence: {
        title: "Diamond Fluorescence",
        body: "Fluorescence describes how a diamond may glow under ultraviolet light. Its effect on appearance varies by diamond and fluorescence intensity."
      },
      color: {
        type: "scale",
        variant: "color",
        title: "Diamond Color",
        intro: "Color grades describe how little body color a diamond shows. Grades closer to D appear more colorless, while lower grades show progressively more warmth.",
        body: "Diamond color grades describe how little body color a diamond shows. For colorless diamonds, grades closer to D appear more colorless, while lower grades may show progressively more warmth.",
        defaultState: "g",
        states: [
          {
            id: "d",
            label: "D",
            heading: "D Color",
            family: "Colorless",
            explanation: "No perceptible body color to the unaided eye — the highest colorless grade."
          },
          {
            id: "e",
            label: "E",
            heading: "E Color",
            family: "Colorless",
            explanation: "Extremely faint traces of color, typically only visible to a trained grader."
          },
          {
            id: "f",
            label: "F",
            heading: "F Color",
            family: "Colorless",
            explanation: "Slight traces of color that remain difficult to detect when viewed face-up."
          },
          {
            id: "g",
            label: "G",
            heading: "G Color",
            family: "Near Colorless",
            explanation: "A faint warmth that is generally difficult to notice in a finished piece."
          },
          {
            id: "h",
            label: "H",
            heading: "H Color",
            family: "Near Colorless",
            explanation: "A subtle warmth that is usually unnoticeable once the diamond is set."
          },
          {
            id: "i",
            label: "I",
            heading: "I Color",
            family: "Near Colorless",
            explanation: "A gentle warmth that may be visible in larger stones or certain settings."
          },
          {
            id: "j",
            label: "J",
            heading: "J Color",
            family: "Near Colorless",
            explanation: "A noticeable warmth that can still appear bright in many settings."
          },
          {
            id: "k",
            label: "K",
            heading: "K Color",
            family: "Faint",
            explanation: "A visible warm tint that is more apparent face-up, especially in larger diamonds."
          }
        ]
      },
      clarity: {
        type: "scale",
        variant: "clarity",
        title: "Diamond Clarity",
        intro: "Clarity describes internal inclusions and external blemishes evaluated under 10× magnification.",
        body: "Clarity describes internal inclusions and external blemishes evaluated under 10× magnification.",
        defaultState: "vs1",
        states: [
          {
            id: "si2",
            label: "SI2",
            heading: "SI2 Clarity",
            family: "Slightly Included 2",
            inspect: { x: 0.579, y: 0.596 },
            explanation: "Noticeable inclusions that may sometimes be visible to the unaided eye, depending on their size and location."
          },
          {
            id: "si1",
            label: "SI1",
            heading: "SI1 Clarity",
            family: "Slightly Included 1",
            inspect: { x: 0.579, y: 0.596 },
            explanation: "Noticeable inclusions that are often difficult to see without magnification, especially once the diamond is set."
          },
          {
            id: "vs2",
            label: "VS2",
            heading: "VS2 Clarity",
            family: "Very Slightly Included 2",
            inspect: { x: 0.579, y: 0.596 },
            explanation: "Minor inclusions that are typically difficult to see with the unaided eye."
          },
          {
            id: "vs1",
            label: "VS1",
            heading: "VS1 Clarity",
            family: "Very Slightly Included 1",
            inspect: { x: 0.579, y: 0.596 },
            explanation: "Small inclusions that are typically not noticeable to the unaided eye."
          },
          {
            id: "vvs2",
            label: "VVS2",
            heading: "VVS2 Clarity",
            family: "Very Very Slightly Included 2",
            inspect: { x: 0.579, y: 0.596 },
            explanation: "Minute inclusions with little to no visible effect on the diamond's appearance."
          },
          {
            id: "vvs1",
            label: "VVS1",
            heading: "VVS1 Clarity",
            family: "Very Very Slightly Included 1",
            inspect: { x: 0.579, y: 0.596 },
            explanation: "Extremely minute inclusions. The diamond appears exceptionally clean."
          },
          {
            id: "if",
            label: "IF",
            heading: "IF Clarity",
            family: "Internally Flawless",
            inspect: { x: 0.579, y: 0.596 },
            explanation: "No internal inclusions are visible. Only minor surface blemishes may be present."
          },
          {
            id: "fl",
            label: "FL",
            heading: "FL Clarity",
            family: "Flawless",
            inspect: { x: 0.579, y: 0.596 },
            explanation: "No inclusions or blemishes are visible. The highest clarity grade."
          }
        ]
      },
      polish: {
        title: "Diamond Polish",
        body: "Polish describes the smoothness and finish of a diamond's facet surfaces after cutting. Higher polish grades indicate fewer surface finishing imperfections."
      },
      symmetry: {
        title: "Diamond Symmetry",
        body: "Symmetry describes how precisely a diamond's facets align with one another. Better symmetry can contribute to a more balanced and consistent appearance."
      },
      ratio: {
        title: "Length-to-Width Ratio",
        body: "Length-to-width ratio compares a diamond's length with its width. It helps describe whether a shape appears more compact, balanced, or elongated."
      },
      table: {
        title: "Table Percentage",
        body: "Table percentage compares the width of the diamond's top table facet with the overall width of the diamond. It is one of the proportions used to evaluate diamond geometry."
      },
      depth: {
        title: "Depth Percentage",
        body: "Depth percentage compares a diamond's total depth with its width. It helps describe how the diamond's weight and proportions are distributed."
      },
      "crown-angle": {
        title: "Crown Angle",
        body: "Crown angle measures the angle of the diamond's upper facets above the girdle. It can influence how light enters and leaves the diamond."
      },
      "pavilion-angle": {
        title: "Pavilion Angle",
        body: "Pavilion angle measures the angle of the lower facets below the girdle. It is an important proportion affecting how light is reflected through the diamond."
      },
      "star-length": {
        title: "Star Length",
        body: "Star length describes the relative length of the star facets surrounding the table. It is one of the proportion measurements used to describe a diamond's facet structure."
      },
      "lower-half": {
        title: "Lower Half",
        body: "Lower-half percentage describes the length of the lower-girdle facets relative to the diamond's structure. It can influence the appearance of the facet pattern."
      },
      girdle: {
        title: "Diamond Girdle",
        body: "The girdle is the outer edge of the diamond where the crown and pavilion meet. Its thickness can vary from extremely thin to extremely thick."
      },
      culet: {
        title: "Diamond Culet",
        body: "The culet is the small facet or point at the bottom of a diamond. Its size can range from none to larger visible facets."
      },
      "cert-gia": {
        title: "GIA Certification",
        body: "The Gemological Institute of America is an independent gemological laboratory that grades and documents a diamond's characteristics."
      },
      "cert-igi": {
        title: "IGI Certification",
        body: "The International Gemological Institute is an independent gemological laboratory that grades and documents a diamond's characteristics."
      },
      "cert-gcal": {
        title: "GCAL Certification",
        body: "Gem Certification & Assurance Lab is an independent gemological laboratory that evaluates and documents a diamond's characteristics."
      }
    };

    var searchRoot = document.getElementById("diamondSearchLayout");
    var shell = document.getElementById("diamondSearchEdu");
    var panel = document.getElementById("diamondSearchEduPanel");
    var titleEl = document.getElementById("diamondSearchEduTitle");
    var bodyEl = document.getElementById("diamondSearchEduBody");
    var moduleHost = document.getElementById("diamondSearchEduModule");
    var backdrop = shell && shell.querySelector(".diamond-search__edu-backdrop");
    var sidebar = searchRoot ? document.getElementById("diamondSearchSidebar") : null;
    if (!shell || !panel || !titleEl || !bodyEl) return function () {};

    function replaceEduTitle(tagName, textValue) {
      var current = document.getElementById("diamondSearchEduTitle") || titleEl;
      var next;
      var parent;
      tagName = String(tagName || "div").toLowerCase();
      if (current && String(current.tagName || "").toLowerCase() === tagName) {
        if (textValue != null) current.textContent = textValue;
        titleEl = current;
        return current;
      }
      next = document.createElement(tagName);
      next.id = "diamondSearchEduTitle";
      next.className = (current && current.className) || "diamond-search__edu-title";
      if (textValue != null) next.textContent = textValue;
      parent = current && current.parentNode;
      if (parent && typeof parent.replaceChild === "function") {
        parent.replaceChild(next, current);
      } else if (parent && typeof parent.appendChild === "function") {
        parent.appendChild(next);
      }
      titleEl = next;
      return next;
    }

    function clearEduTitle() {
      var el = replaceEduTitle("div", "");
      if (el) el.hidden = true;
      return el;
    }

    function setEduTitle(textValue) {
      var el = replaceEduTitle("h2", textValue || "");
      if (el) el.hidden = false;
      return el;
    }

    if (
      titleEl &&
      /^(H1|H2|H3|H4|H5|H6)$/.test(titleEl.tagName || "") &&
      !String(titleEl.textContent || "").trim()
    ) {
      clearEduTitle();
    }

    if (shell.parentNode !== document.body) {
      document.body.appendChild(shell);
    }

    var mqMobile = window.matchMedia("(max-width: 989px)");
    var activeTrigger = null;
    var pendingEduContext = null;
    var activeScaleContent = null;
    var scaleApplyLock = false;
    var cutPointerBound = null;
    var activeCutStateId = "";
    var CUT_AEC_HANDLE_SIZE_PX = 18;
    var CUT_AEC_TRAVEL_INSET_PX = CUT_AEC_HANDLE_SIZE_PX / 2;
    var CUT_AEC_BUBBLE_HALF_PX = 44;
    var colorPointerBound = null;
    var activeColorStateId = "";
    var COLOR_AEC_HANDLE_SIZE_PX = 18;
    var COLOR_AEC_TRAVEL_INSET_PX = COLOR_AEC_HANDLE_SIZE_PX / 2;
    var COLOR_AEC_BUBBLE_HALF_PX = 20;
    var clarityPointerBound = null;
    var activeClarityStateId = "";
    var CLARITY_AEC_HANDLE_SIZE_PX = 18;
    var CLARITY_AEC_TRAVEL_INSET_PX = CLARITY_AEC_HANDLE_SIZE_PX / 2;
    var CLARITY_AEC_BUBBLE_HALF_PX = 24;
    var caratPointerBound = null;
    var caratTonePointerBound = null;
    var caratToneFadeGen = 0;
    var caratTonePreloadMap = {};
    var cleanups = [];

    function listen(target, type, handler, opts) {
      if (!target) return;
      target.addEventListener(type, handler, opts);
      var capture = opts === true || !!(opts && opts.capture);
      cleanups.push(function () {
        target.removeEventListener(type, handler, capture);
      });
    }

    function isMobile() {
      return mqMobile.matches;
    }

    function isScaleContent(content) {
      return !!(content && content.type === "scale" && content.states && content.states.length);
    }

    function isColorScaleContent(content) {
      return isScaleContent(content) && content.variant === "color";
    }

    function isClarityScaleContent(content) {
      return isScaleContent(content) && content.variant === "clarity";
    }

    function isCaratScaleContent(content) {
      return isScaleContent(content) && content.variant === "carat";
    }

    function isCutScaleContent(content) {
      return (
        isScaleContent(content) &&
        !isColorScaleContent(content) &&
        !isClarityScaleContent(content) &&
        !isCaratScaleContent(content)
      );
    }

    function getScaleRadioName(content) {
      if (isColorScaleContent(content)) return "diamondSearchEduColorScale";
      if (isClarityScaleContent(content)) return "diamondSearchEduClarityScale";
      if (isCaratScaleContent(content)) return "diamondSearchEduCaratScale";
      return "diamondSearchEduScale";
    }

    function getScaleVisualUrl(state) {
      if (!moduleHost || !state || !state.id) return "";
      return moduleHost.getAttribute("data-edu-cut-" + state.id) || "";
    }

    function getColorVisualUrl(state) {
      if (!moduleHost || !state || !state.id) return "";
      return moduleHost.getAttribute("data-edu-color-" + state.id) || "";
    }

    function getClarityMasterUrl() {
      if (!moduleHost) return "";
      return moduleHost.getAttribute("data-edu-clarity-master") || "";
    }

    function getClarityDiamondUrl(stateId) {
      if (!moduleHost || !stateId) return getClarityMasterUrl();
      return moduleHost.getAttribute("data-edu-clarity-" + stateId) || getClarityMasterUrl();
    }

    function getClarityLoupeUrl(stateId) {
      if (!moduleHost) return "";
      if (stateId) {
        var gradeUrl = moduleHost.getAttribute("data-edu-clarity-" + stateId + "-10x");
        if (gradeUrl) return gradeUrl;
      }
      return moduleHost.getAttribute("data-edu-clarity-10x") || "";
    }

    function getClarityDiamondKey(stateId) {
      var url = getClarityDiamondUrl(stateId);
      var master = getClarityMasterUrl();
      if (!url || url === master) return "master";
      return stateId;
    }

    function getClarityLoupeKey(stateId) {
      if (!moduleHost || !stateId) return "master";
      if (moduleHost.getAttribute("data-edu-clarity-" + stateId + "-10x")) return stateId;
      return "master";
    }

    function getCaratHandUrl() {
      if (!moduleHost) return "";
      return moduleHost.getAttribute("data-edu-carat-hand") || "";
    }

    function getCaratDiamondUrl(shape) {
      if (!moduleHost) return "";
      if (shape === "oval") {
        return moduleHost.getAttribute("data-edu-carat-diamond-oval") || "";
      }
      if (shape === "pear") {
        return moduleHost.getAttribute("data-edu-carat-diamond-pear") || "";
      }
      if (shape === "marquise") {
        return moduleHost.getAttribute("data-edu-carat-diamond-marquise") || "";
      }
      if (shape === "cushion") {
        return moduleHost.getAttribute("data-edu-carat-diamond-cushion") || "";
      }
      if (shape === "princess") {
        return moduleHost.getAttribute("data-edu-carat-diamond-princess") || "";
      }
      if (shape === "emerald") {
        return moduleHost.getAttribute("data-edu-carat-diamond-emerald") || "";
      }
      if (shape === "radiant") {
        return moduleHost.getAttribute("data-edu-carat-diamond-radiant") || "";
      }
      if (shape === "heart") {
        return moduleHost.getAttribute("data-edu-carat-diamond-heart") || "";
      }
      if (shape === "asscher") {
        return moduleHost.getAttribute("data-edu-carat-diamond-asscher") || "";
      }
      return moduleHost.getAttribute("data-edu-carat-diamond") || "";
    }

    function getActiveSearchShape() {
      var btn = document.querySelector("#diamondSearchLayout .shape-scroll-container button.active");
      var raw = btn ? btn.getAttribute("data-shape") : "";
      if (!raw && pendingEduContext && pendingEduContext.shape) raw = pendingEduContext.shape;
      return requireCaratAec().resolveSearchShape(raw);
    }

    function readEduContext(trigger) {
      return {
        stateId: trigger && trigger.getAttribute
          ? String(trigger.getAttribute("data-edu-state") || "").trim().toLowerCase()
          : "",
        carat: trigger && trigger.getAttribute ? String(trigger.getAttribute("data-edu-carat") || "").trim() : "",
        shape: trigger && trigger.getAttribute ? String(trigger.getAttribute("data-edu-shape") || "").trim() : "",
      };
    }

    function resolveContextStateId(content) {
      var wanted = pendingEduContext && pendingEduContext.stateId;
      var i;
      if (wanted && content && content.states) {
        for (i = 0; i < content.states.length; i++) {
          if (content.states[i].id === wanted) return wanted;
        }
      }
      return (content && content.defaultState) || (content && content.states && content.states[0] && content.states[0].id) || "";
    }

    function focusNoScroll(el) {
      if (!el) return;
      try {
        el.focus({ preventScroll: true });
      } catch (err) {
        el.focus();
      }
    }

    function setTriggerExpanded(trigger, expanded) {
      if (trigger) trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
    }

    function clearPanelPosition() {
      panel.style.top = "";
      panel.style.left = "";
      panel.style.right = "";
      panel.style.bottom = "";
      panel.style.width = "";
    }

    function clearScaleModule() {
      activeScaleContent = null;
      cutPointerBound = null;
      activeCutStateId = "";
      colorPointerBound = null;
      activeColorStateId = "";
      clarityPointerBound = null;
      activeClarityStateId = "";
      caratPointerBound = null;
      caratTonePointerBound = null;
      caratToneFadeGen += 1;
      if (moduleHost) {
        moduleHost.hidden = true;
        moduleHost.innerHTML = "";
      }
      panel.classList.remove("diamond-search__edu-panel--module");
    }

    function positionPanel() {
      if (!activeTrigger) return;
      if (isMobile()) {
        clearPanelPosition();
        return;
      }

      var moduleOpen = panel.classList.contains("diamond-search__edu-panel--module");
      if (moduleOpen) {
        clearPanelPosition();
        return;
      }

      var rect = activeTrigger.getBoundingClientRect();
      var gap = 8;
      var width = Math.min(320, Math.max(280, window.innerWidth - 24));
      var viewportPad = 12;
      panel.style.width = width + "px";

      var left = rect.right + gap;
      if (left + width > window.innerWidth - viewportPad) {
        left = rect.left - width - gap;
      }
      if (left < viewportPad) left = viewportPad;

      var top = rect.top;
      var height = panel.offsetHeight || 160;
      if (top + height > window.innerHeight - viewportPad) {
        top = window.innerHeight - height - viewportPad;
      }
      if (top < viewportPad) top = viewportPad;

      panel.style.left = Math.round(left) + "px";
      panel.style.top = Math.round(top) + "px";
      panel.style.right = "";
      panel.style.bottom = "";
    }

    function triggerIsVisible() {
      if (!activeTrigger || !sidebar) return true;
      var sidebarRect = sidebar.getBoundingClientRect();
      var triggerRect = activeTrigger.getBoundingClientRect();
      return (
        triggerRect.bottom > sidebarRect.top + 4 &&
        triggerRect.top < sidebarRect.bottom - 4 &&
        triggerRect.right > sidebarRect.left &&
        triggerRect.left < sidebarRect.right
      );
    }

    function setScaleExplanation(el, explanation) {
      if (!el) return;
      var text = explanation || "";
      var splitAt = text.indexOf(". ");
      var lead = splitAt > 0 ? text.slice(0, splitAt + 1) : text;
      var rest = splitAt > 0 ? text.slice(splitAt + 2) : "";
      var leadEl;

      el.textContent = "";
      if (lead) {
        leadEl = document.createElement("span");
        leadEl.className = "diamond-search__edu-scale-explain-lead";
        leadEl.textContent = lead;
        el.appendChild(leadEl);
      }
      if (rest) {
        el.appendChild(document.createTextNode(" " + rest));
      }
    }

    function cutAecStateIndex(content, stateId) {
      var states = content && content.states ? content.states : [];
      var i;
      for (i = 0; i < states.length; i++) {
        if (states[i].id === stateId) return i;
      }
      return 0;
    }

    function cutAecToTrackPos(index, last) {
      var n = Number(index);
      var end = Number(last);
      if (!isFinite(n) || n < 0) n = 0;
      if (!isFinite(end) || end <= 0) return 0;
      if (n > end) n = end;
      return n / end;
    }

    function cutAecFromTrackPos(pos, content) {
      var states = content && content.states ? content.states : [];
      var last = states.length - 1;
      var t = Number(pos);
      var index;
      if (last < 0) return "";
      if (!isFinite(t)) t = 0;
      if (t < 0) t = 0;
      if (t > 1) t = 1;
      index = Math.round(t * last);
      if (index < 0) index = 0;
      if (index > last) index = last;
      return states[index] ? states[index].id : "";
    }

    function cutAecThumbCenterPx(trackPos, trackWidth) {
      var p = Number(trackPos);
      var w = Number(trackWidth);
      var inset = CUT_AEC_TRAVEL_INSET_PX;
      if (!isFinite(p)) p = 0;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      if (!(w > 0)) return inset;
      return inset + p * Math.max(0, w - 2 * inset);
    }

    function cutAecVisualTrackPos(trackPos, trackWidth) {
      var w = Number(trackWidth);
      var p = Number(trackPos);
      if (!isFinite(p)) p = 0;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      if (!isFinite(w) || w <= 0) return p;
      return cutAecThumbCenterPx(p, w) / w;
    }

    function cutAecPosFromClientX(slider, clientX, currentId, content) {
      var track = slider && slider.querySelector ? slider.querySelector("[data-edu-cut-track]") : null;
      var rect = track && track.getBoundingClientRect
        ? track.getBoundingClientRect()
        : slider && slider.getBoundingClientRect
          ? slider.getBoundingClientRect()
          : null;
      var inset = CUT_AEC_TRAVEL_INSET_PX;
      var usable;
      var t;
      if (!rect || !rect.width) {
        return cutAecToTrackPos(cutAecStateIndex(content, currentId), (content.states.length || 1) - 1);
      }
      usable = Math.max(1, rect.width - 2 * inset);
      t = (clientX - rect.left - inset) / usable;
      if (t < 0) t = 0;
      if (t > 1) t = 1;
      return t;
    }

    function stepCutAecState(content, currentId, delta) {
      var states = content && content.states ? content.states : [];
      var last = states.length - 1;
      var index = cutAecStateIndex(content, currentId);
      if (last < 0) return currentId;
      index += delta < 0 ? -1 : 1;
      if (index < 0) index = 0;
      if (index > last) index = last;
      return states[index].id;
    }

    function applyCutAecBubbleLayout(slider, pos) {
      var layout;
      var half;
      var bubble;
      var width;
      var aec;
      if (!slider) return;
      bubble = slider.querySelector ? slider.querySelector("[data-edu-cut-bubble]") : null;
      width = bubble && Number(bubble.offsetWidth);
      half =
        isFinite(width) && width > 0
          ? width / 2
          : CUT_AEC_BUBBLE_HALF_PX;
      aec = window.IvanovCaratAec;
      if (aec && typeof aec.clampBubbleLayout === "function") {
        layout = aec.clampBubbleLayout(cutAecVisualTrackPos(pos, slider.clientWidth), slider.clientWidth, half);
      } else {
        layout = {
          leftPx: null,
          leftPct: cutAecVisualTrackPos(pos, slider.clientWidth) * 100,
          pointerShiftPx: 0
        };
      }
      if (!slider.style || typeof slider.style.setProperty !== "function") return;
      if (layout.leftPx != null) {
        slider.style.setProperty("--edu-cut-bubble-left", layout.leftPx + "px");
      } else {
        slider.style.setProperty("--edu-cut-bubble-left", layout.leftPct + "%");
      }
      slider.style.setProperty("--edu-cut-bubble-pointer-shift", layout.pointerShiftPx + "px");
    }

    function colorAecStateIndex(content, stateId) {
      var states = content && content.states ? content.states : [];
      var i;
      for (i = 0; i < states.length; i++) {
        if (states[i].id === stateId) return i;
      }
      return 0;
    }

    function colorAecToTrackPos(index, last) {
      var n = Number(index);
      var end = Number(last);
      if (!isFinite(n) || n < 0) n = 0;
      if (!isFinite(end) || end <= 0) return 0;
      if (n > end) n = end;
      return n / end;
    }

    function colorAecFromTrackPos(pos, content) {
      var states = content && content.states ? content.states : [];
      var last = states.length - 1;
      var t = Number(pos);
      var index;
      if (last < 0) return "";
      if (!isFinite(t)) t = 0;
      if (t < 0) t = 0;
      if (t > 1) t = 1;
      index = Math.round(t * last);
      if (index < 0) index = 0;
      if (index > last) index = last;
      return states[index] ? states[index].id : "";
    }

    function colorAecThumbCenterPx(trackPos, trackWidth) {
      var p = Number(trackPos);
      var w = Number(trackWidth);
      var inset = COLOR_AEC_TRAVEL_INSET_PX;
      if (!isFinite(p)) p = 0;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      if (!(w > 0)) return inset;
      return inset + p * Math.max(0, w - 2 * inset);
    }

    function colorAecVisualTrackPos(trackPos, trackWidth) {
      var w = Number(trackWidth);
      var p = Number(trackPos);
      if (!isFinite(p)) p = 0;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      if (!isFinite(w) || w <= 0) return p;
      return colorAecThumbCenterPx(p, w) / w;
    }

    function colorAecPosFromClientX(slider, clientX, currentId, content) {
      var track = slider && slider.querySelector ? slider.querySelector("[data-edu-color-track]") : null;
      var rect = track && track.getBoundingClientRect
        ? track.getBoundingClientRect()
        : slider && slider.getBoundingClientRect
          ? slider.getBoundingClientRect()
          : null;
      var inset = COLOR_AEC_TRAVEL_INSET_PX;
      var usable;
      var t;
      if (!rect || !rect.width) {
        return colorAecToTrackPos(colorAecStateIndex(content, currentId), (content.states.length || 1) - 1);
      }
      usable = Math.max(1, rect.width - 2 * inset);
      t = (clientX - rect.left - inset) / usable;
      if (t < 0) t = 0;
      if (t > 1) t = 1;
      return t;
    }

    function stepColorAecState(content, currentId, delta) {
      var states = content && content.states ? content.states : [];
      var last = states.length - 1;
      var index = colorAecStateIndex(content, currentId);
      if (last < 0) return currentId;
      index += delta < 0 ? -1 : 1;
      if (index < 0) index = 0;
      if (index > last) index = last;
      return states[index].id;
    }

    function applyColorAecBubbleLayout(slider, pos) {
      var layout;
      var half;
      var bubble;
      var width;
      var aec;
      if (!slider) return;
      bubble = slider.querySelector ? slider.querySelector("[data-edu-color-bubble]") : null;
      width = bubble && Number(bubble.offsetWidth);
      half =
        isFinite(width) && width > 0
          ? width / 2
          : COLOR_AEC_BUBBLE_HALF_PX;
      aec = window.IvanovCaratAec;
      if (aec && typeof aec.clampBubbleLayout === "function") {
        layout = aec.clampBubbleLayout(colorAecVisualTrackPos(pos, slider.clientWidth), slider.clientWidth, half);
      } else {
        layout = {
          leftPx: null,
          leftPct: colorAecVisualTrackPos(pos, slider.clientWidth) * 100,
          pointerShiftPx: 0
        };
      }
      if (!slider.style || typeof slider.style.setProperty !== "function") return;
      if (layout.leftPx != null) {
        slider.style.setProperty("--edu-color-bubble-left", layout.leftPx + "px");
      } else {
        slider.style.setProperty("--edu-color-bubble-left", layout.leftPct + "%");
      }
      slider.style.setProperty("--edu-color-bubble-pointer-shift", layout.pointerShiftPx + "px");
    }

    function clarityAecStateIndex(content, stateId) {
      var states = content && content.states ? content.states : [];
      var i;
      for (i = 0; i < states.length; i++) {
        if (states[i].id === stateId) return i;
      }
      return 0;
    }

    function clarityAecToTrackPos(index, last) {
      var n = Number(index);
      var end = Number(last);
      if (!isFinite(n) || n < 0) n = 0;
      if (!isFinite(end) || end <= 0) return 0;
      if (n > end) n = end;
      return n / end;
    }

    function clarityAecFromTrackPos(pos, content) {
      var states = content && content.states ? content.states : [];
      var last = states.length - 1;
      var t = Number(pos);
      var index;
      if (last < 0) return "";
      if (!isFinite(t)) t = 0;
      if (t < 0) t = 0;
      if (t > 1) t = 1;
      index = Math.round(t * last);
      if (index < 0) index = 0;
      if (index > last) index = last;
      return states[index] ? states[index].id : "";
    }

    function clarityAecThumbCenterPx(trackPos, trackWidth) {
      var p = Number(trackPos);
      var w = Number(trackWidth);
      var inset = CLARITY_AEC_TRAVEL_INSET_PX;
      if (!isFinite(p)) p = 0;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      if (!(w > 0)) return inset;
      return inset + p * Math.max(0, w - 2 * inset);
    }

    function clarityAecVisualTrackPos(trackPos, trackWidth) {
      var w = Number(trackWidth);
      var p = Number(trackPos);
      if (!isFinite(p)) p = 0;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      if (!isFinite(w) || w <= 0) return p;
      return clarityAecThumbCenterPx(p, w) / w;
    }

    function clarityAecPosFromClientX(slider, clientX, currentId, content) {
      var track = slider && slider.querySelector ? slider.querySelector("[data-edu-clarity-track]") : null;
      var rect = track && track.getBoundingClientRect
        ? track.getBoundingClientRect()
        : slider && slider.getBoundingClientRect
          ? slider.getBoundingClientRect()
          : null;
      var inset = CLARITY_AEC_TRAVEL_INSET_PX;
      var usable;
      var t;
      if (!rect || !rect.width) {
        return clarityAecToTrackPos(clarityAecStateIndex(content, currentId), (content.states.length || 1) - 1);
      }
      usable = Math.max(1, rect.width - 2 * inset);
      t = (clientX - rect.left - inset) / usable;
      if (t < 0) t = 0;
      if (t > 1) t = 1;
      return t;
    }

    function stepClarityAecState(content, currentId, delta) {
      var states = content && content.states ? content.states : [];
      var last = states.length - 1;
      var index = clarityAecStateIndex(content, currentId);
      if (last < 0) return currentId;
      index += delta < 0 ? -1 : 1;
      if (index < 0) index = 0;
      if (index > last) index = last;
      return states[index].id;
    }

    function applyClarityAecBubbleLayout(slider, pos) {
      var layout;
      var half;
      var bubble;
      var width;
      var aec;
      if (!slider) return;
      bubble = slider.querySelector ? slider.querySelector("[data-edu-clarity-bubble]") : null;
      width = bubble && Number(bubble.offsetWidth);
      half =
        isFinite(width) && width > 0
          ? width / 2
          : CLARITY_AEC_BUBBLE_HALF_PX;
      aec = window.IvanovCaratAec;
      if (aec && typeof aec.clampBubbleLayout === "function") {
        layout = aec.clampBubbleLayout(clarityAecVisualTrackPos(pos, slider.clientWidth), slider.clientWidth, half);
      } else {
        layout = {
          leftPx: null,
          leftPct: clarityAecVisualTrackPos(pos, slider.clientWidth) * 100,
          pointerShiftPx: 0
        };
      }
      if (!slider.style || typeof slider.style.setProperty !== "function") return;
      if (layout.leftPx != null) {
        slider.style.setProperty("--edu-clarity-bubble-left", layout.leftPx + "px");
      } else {
        slider.style.setProperty("--edu-clarity-bubble-left", layout.leftPct + "%");
      }
      slider.style.setProperty("--edu-clarity-bubble-pointer-shift", layout.pointerShiftPx + "px");
    }

    function applyScaleState(content, stateId) {
      if (!moduleHost || !content || !content.states) return;
      var states = content.states;
      var index = 0;
      var state = states[0];
      var last = states.length - 1;
      var pos;
      var fillPct;
      var i;
      var tickId;
      for (i = 0; i < states.length; i++) {
        if (states[i].id === stateId) {
          index = i;
          state = states[i];
          break;
        }
      }

      var visual = moduleHost.querySelector("[data-edu-scale-visual]");
      var caption = moduleHost.querySelector("[data-edu-scale-caption]");
      var explain = moduleHost.querySelector("[data-edu-scale-explain]");
      var slider = moduleHost.querySelector("[data-edu-cut-slider]");
      var bubble = moduleHost.querySelector("[data-edu-cut-bubble]");
      var ticks = moduleHost.querySelectorAll("[data-edu-cut-tick]");

      activeCutStateId = state.id;
      pos = cutAecToTrackPos(index, last);
      fillPct = pos * 100;

      if (visual) visual.setAttribute("data-edu-scale-state", state.id);
      var imgs = moduleHost.querySelectorAll("[data-edu-scale-img]");
      for (i = 0; i < imgs.length; i++) {
        if (imgs[i].getAttribute("data-edu-scale-img") === state.id) {
          imgs[i].classList.add("is-active");
        } else {
          imgs[i].classList.remove("is-active");
        }
      }
      if (caption) caption.textContent = state.label;
      setScaleExplanation(explain, state.explanation);
      if (bubble) bubble.textContent = state.label;
      if (slider) {
        if (slider.style && typeof slider.style.setProperty === "function") {
          slider.style.setProperty("--edu-cut-n", String(pos));
          slider.style.setProperty("--edu-cut-track-pct", fillPct + "%");
        }
        applyCutAecBubbleLayout(slider, pos);
        slider.setAttribute("aria-valuemin", "0");
        slider.setAttribute("aria-valuemax", String(Math.max(0, last)));
        slider.setAttribute("aria-valuenow", String(index));
        slider.setAttribute("aria-valuetext", state.label);
      }
      for (i = 0; i < ticks.length; i++) {
        tickId = ticks[i].getAttribute("data-edu-cut-tick");
        if (tickId === state.id) ticks[i].setAttribute("aria-current", "true");
        else ticks[i].removeAttribute("aria-current");
      }
    }

    function renderScaleModule(content) {
      if (!moduleHost) throw new Error("edu-module-host-missing");

      var defaultId = resolveContextStateId(content);
      var groupLabel = escapeHtml(content.title || "Cut") + " grades";
      var html = [];
      var i;
      var state;
      var visualUrl;
      var last = content.states.length - 1;
      var defaultIndex = cutAecStateIndex(content, defaultId);
      var pos = cutAecToTrackPos(defaultIndex, last);
      var fillPct = pos * 100;
      var tickPos;

      html.push('<div class="diamond-search__edu-scale">');
      html.push('<div class="diamond-search__edu-scale-visual" data-edu-scale-visual aria-hidden="true">');
      html.push('<div class="diamond-search__edu-scale-frame">');
      html.push('<div class="diamond-search__edu-scale-media">');
      for (i = 0; i < content.states.length; i++) {
        state = content.states[i];
        visualUrl = getScaleVisualUrl(state);
        if (!visualUrl) continue;
        html.push(
          '<img class="diamond-search__edu-scale-img' +
            (state.id === defaultId ? " is-active" : "") +
            '" data-edu-scale-img="' +
            escapeHtml(state.id) +
            '" src="' +
            escapeHtml(visualUrl) +
            '" width="1536" height="1024" alt="" decoding="async">'
        );
      }
      html.push("</div>");
      html.push('<span class="diamond-search__edu-scale-caption" data-edu-scale-caption></span>');
      html.push("</div></div>");

      html.push('<div class="diamond-search__edu-scale-control">');
      html.push(
        '<div class="diamond-search__edu-cut-slider" data-edu-cut-slider role="slider" tabindex="0" aria-label="' +
          groupLabel +
          '" aria-valuemin="0" aria-valuemax="' +
          last +
          '" aria-valuenow="' +
          defaultIndex +
          '" aria-valuetext="' +
          escapeHtml(content.states[defaultIndex].label) +
          '" style="--edu-cut-n:' +
          pos +
          ';--edu-cut-track-pct:' +
          fillPct +
          '%;--edu-cut-bubble-pointer-shift:0px">'
      );
      html.push(
        '<div class="diamond-search__edu-cut-bubble" data-edu-cut-bubble aria-hidden="true">' +
          escapeHtml(content.states[defaultIndex].label) +
          "</div>"
      );
      html.push('<div class="diamond-search__edu-cut-track" data-edu-cut-track>');
      html.push('<span class="diamond-search__edu-cut-rail" aria-hidden="true"></span>');
      html.push('<span class="diamond-search__edu-cut-fill" data-edu-cut-fill aria-hidden="true"></span>');
      html.push('<span class="diamond-search__edu-cut-handle" data-edu-cut-handle aria-hidden="true"></span>');
      html.push("</div>");
      html.push('<div class="diamond-search__edu-cut-ticks" role="presentation">');
      for (i = 0; i < content.states.length; i++) {
        state = content.states[i];
        tickPos = last <= 0 ? 0 : i / last;
        html.push(
          '<button type="button" class="diamond-search__edu-cut-tick" data-edu-cut-tick="' +
            escapeHtml(state.id) +
            '"' +
            (state.id === defaultId ? ' aria-current="true"' : "") +
            ' tabindex="-1" style="--edu-cut-tick-n:' +
            tickPos +
            '" aria-label="Set cut grade to ' +
            escapeHtml(state.label) +
            '"><span class="diamond-search__edu-cut-tick-mark" aria-hidden="true"></span><span class="diamond-search__edu-cut-tick-label">' +
            escapeHtml(state.label) +
            "</span></button>"
        );
      }
      html.push("</div></div></div>");
      html.push(
        '<p class="diamond-search__edu-scale-explain" id="diamondSearchEduExplain" data-edu-scale-explain aria-live="polite"></p>'
      );
      html.push("</div>");

      moduleHost.innerHTML = html.join("");
      moduleHost.hidden = false;
      panel.classList.add("diamond-search__edu-panel--module");
      activeScaleContent = content;
      applyScaleState(content, defaultId);
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(function () {
          if (activeScaleContent === content) applyScaleState(content, defaultId);
        });
      }
    }

    function applyColorState(content, stateId) {
      if (!moduleHost || !content || !content.states) return;
      var states = content.states;
      var index = 0;
      var state = states[0];
      var last = states.length - 1;
      var pos;
      var fillPct;
      var i;
      var tickId;
      for (i = 0; i < states.length; i++) {
        if (states[i].id === stateId) {
          index = i;
          state = states[i];
          break;
        }
      }

      var visual = moduleHost.querySelector("[data-edu-color-visual]");
      var heading = moduleHost.querySelector("[data-edu-color-heading]");
      var family = moduleHost.querySelector("[data-edu-color-family]");
      var explain = moduleHost.querySelector("[data-edu-color-explain]");
      var slider = moduleHost.querySelector("[data-edu-color-slider]");
      var bubble = moduleHost.querySelector("[data-edu-color-bubble]");
      var ticks = moduleHost.querySelectorAll("[data-edu-color-tick]");

      activeColorStateId = state.id;
      pos = colorAecToTrackPos(index, last);
      fillPct = pos * 100;

      if (visual) visual.setAttribute("data-edu-color-grade", state.id);
      var imgs = moduleHost.querySelectorAll("[data-edu-color-img]");
      for (i = 0; i < imgs.length; i++) {
        if (imgs[i].getAttribute("data-edu-color-img") === state.id) {
          imgs[i].classList.add("is-active");
        } else {
          imgs[i].classList.remove("is-active");
        }
      }
      if (heading) heading.textContent = state.heading || (state.label + " Color");
      if (family) family.textContent = state.family || "";
      setScaleExplanation(explain, state.explanation);
      if (bubble) bubble.textContent = state.label;
      if (slider) {
        if (slider.style && typeof slider.style.setProperty === "function") {
          slider.style.setProperty("--edu-color-n", String(pos));
          slider.style.setProperty("--edu-color-track-pct", fillPct + "%");
        }
        applyColorAecBubbleLayout(slider, pos);
        slider.setAttribute("aria-valuemin", "0");
        slider.setAttribute("aria-valuemax", String(Math.max(0, last)));
        slider.setAttribute("aria-valuenow", String(index));
        slider.setAttribute("aria-valuetext", state.label);
      }
      for (i = 0; i < ticks.length; i++) {
        tickId = ticks[i].getAttribute("data-edu-color-tick");
        if (tickId === state.id) ticks[i].setAttribute("aria-current", "true");
        else ticks[i].removeAttribute("aria-current");
      }
    }

    function renderColorModule(content) {
      if (!moduleHost) throw new Error("edu-module-host-missing");

      var defaultId = resolveContextStateId(content);
      var groupLabel = "Color grades";
      var html = [];
      var i;
      var state;
      var visualUrl;
      var sizerUrl = getColorVisualUrl({ id: defaultId });
      var last = content.states.length - 1;
      var defaultIndex = colorAecStateIndex(content, defaultId);
      var pos = colorAecToTrackPos(defaultIndex, last);
      var fillPct = pos * 100;
      var tickPos;

      html.push('<div class="diamond-search__edu-color" data-edu-color>');
      html.push('<div class="diamond-search__edu-color-visual" data-edu-color-visual aria-hidden="true">');
      html.push('<div class="diamond-search__edu-color-stage">');
      html.push('<div class="diamond-search__edu-color-media">');
      if (sizerUrl) {
        html.push(
          '<img class="diamond-search__edu-color-sizer" src="' +
            escapeHtml(sizerUrl) +
            '" width="960" height="564" alt="" decoding="async" aria-hidden="true">'
        );
      }
      for (i = 0; i < content.states.length; i++) {
        state = content.states[i];
        visualUrl = getColorVisualUrl(state);
        if (!visualUrl) continue;
        html.push(
          '<img class="diamond-search__edu-color-diamond' +
            (state.id === defaultId ? " is-active" : "") +
            '" data-edu-color-img="' +
            escapeHtml(state.id) +
            '" src="' +
            escapeHtml(visualUrl) +
            '" width="960" height="564" alt="" decoding="async">'
        );
      }
      html.push("</div></div></div>");

      html.push('<div class="diamond-search__edu-color-info">');
      html.push('<p class="diamond-search__edu-color-heading" data-edu-color-heading></p>');
      html.push('<p class="diamond-search__edu-color-family" data-edu-color-family></p>');
      html.push("</div>");

      html.push('<div class="diamond-search__edu-color-scale">');
      html.push(
        '<div class="diamond-search__edu-color-slider" data-edu-color-slider role="slider" tabindex="0" aria-label="' +
          groupLabel +
          '" aria-valuemin="0" aria-valuemax="' +
          last +
          '" aria-valuenow="' +
          defaultIndex +
          '" aria-valuetext="' +
          escapeHtml(content.states[defaultIndex].label) +
          '" style="--edu-color-n:' +
          pos +
          ';--edu-color-track-pct:' +
          fillPct +
          '%;--edu-color-bubble-pointer-shift:0px">'
      );
      html.push(
        '<div class="diamond-search__edu-color-bubble" data-edu-color-bubble aria-hidden="true">' +
          escapeHtml(content.states[defaultIndex].label) +
          "</div>"
      );
      html.push('<div class="diamond-search__edu-color-track" data-edu-color-track>');
      html.push('<span class="diamond-search__edu-color-rail" aria-hidden="true"></span>');
      html.push('<span class="diamond-search__edu-color-fill" data-edu-color-fill aria-hidden="true"></span>');
      html.push('<span class="diamond-search__edu-color-handle" data-edu-color-handle aria-hidden="true"></span>');
      html.push("</div>");
      html.push('<div class="diamond-search__edu-color-ticks" role="presentation">');
      for (i = 0; i < content.states.length; i++) {
        state = content.states[i];
        tickPos = last <= 0 ? 0 : i / last;
        html.push(
          '<button type="button" class="diamond-search__edu-color-tick" data-edu-color-tick="' +
            escapeHtml(state.id) +
            '"' +
            (state.id === defaultId ? ' aria-current="true"' : "") +
            ' tabindex="-1" style="--edu-color-tick-n:' +
            tickPos +
            '" aria-label="Set color grade to ' +
            escapeHtml(state.label) +
            '"><span class="diamond-search__edu-color-tick-mark" aria-hidden="true"></span><span class="diamond-search__edu-color-tick-label">' +
            escapeHtml(state.label) +
            "</span></button>"
        );
      }
      html.push("</div></div></div>");
      html.push(
        '<p class="diamond-search__edu-scale-explain" id="diamondSearchEduColorExplain" data-edu-color-explain aria-live="polite"></p>'
      );
      html.push("</div>");

      moduleHost.innerHTML = html.join("");
      moduleHost.hidden = false;
      panel.classList.add("diamond-search__edu-panel--module");
      activeScaleContent = content;
      applyColorState(content, defaultId);
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(function () {
          if (activeScaleContent === content) applyColorState(content, defaultId);
        });
      }
    }

    function collectClarityImgLayers(kind) {
      var layers = [];
      var seen = {};
      var i;
      var id;
      var url;
      var ids = ["si2", "si1", "vs2", "vs1", "vvs2", "vvs1", "if", "fl"];

      function add(key, src, width, height) {
        if (!src || seen[src]) return;
        seen[src] = true;
        layers.push({ key: key, src: src, width: width, height: height });
      }

      if (kind === "loupe") {
        add("master", getClarityLoupeUrl(""), 360, 360);
        for (i = 0; i < ids.length; i++) {
          id = ids[i];
          url = moduleHost ? moduleHost.getAttribute("data-edu-clarity-" + id + "-10x") : "";
          add(id, url, 360, 360);
        }
      } else {
        add("master", getClarityMasterUrl(), 960, 564);
        for (i = 0; i < ids.length; i++) {
          id = ids[i];
          url = moduleHost ? moduleHost.getAttribute("data-edu-clarity-" + id) : "";
          add(id, url, 960, 564);
        }
      }
      return layers;
    }

    function updateClarityOptics(state) {
      if (!moduleHost || !state) return;
      var loupe = moduleHost.querySelector("[data-edu-clarity-loupe]");
      var focus = moduleHost.querySelector("[data-edu-clarity-focus]");
      var leader = moduleHost.querySelector("[data-edu-clarity-leader]");
      var line = moduleHost.querySelector("[data-edu-clarity-leader-line]");
      var inspect = state.inspect || { x: 0.579, y: 0.596 };
      var stage;
      var stageRect;
      var focusRect;
      var loupeRect;
      var x1;
      var y1;
      var x2;
      var y2;
      var dx;
      var dy;
      var dist;
      var rFocus;
      var rLoupe;

      if (focus) {
        focus.setAttribute("cx", String(inspect.x * 960));
        focus.setAttribute("cy", String(inspect.y * 564));
        focus.setAttribute("opacity", state.id === "fl" ? "0.45" : "0.75");
      }

      if (leader && line && loupe && focus) {
        stage = leader.parentNode;
        if (!stage) return;
        stageRect = stage.getBoundingClientRect();
        focusRect = focus.getBoundingClientRect();
        loupeRect = loupe.getBoundingClientRect();
        if (!stageRect.width || !focusRect.width || !loupeRect.width) return;

        leader.setAttribute("viewBox", "0 0 " + stageRect.width + " " + stageRect.height);
        leader.setAttribute("width", String(stageRect.width));
        leader.setAttribute("height", String(stageRect.height));

        x1 = focusRect.left + focusRect.width / 2 - stageRect.left;
        y1 = focusRect.top + focusRect.height / 2 - stageRect.top;
        x2 = loupeRect.left + loupeRect.width / 2 - stageRect.left;
        y2 = loupeRect.top + loupeRect.height / 2 - stageRect.top;
        dx = x2 - x1;
        dy = y2 - y1;
        dist = Math.sqrt(dx * dx + dy * dy) || 1;
        rFocus = Math.max(focusRect.width, focusRect.height) / 2;
        rLoupe = loupeRect.width / 2 + 1;
        line.setAttribute("x1", String(x1 + (dx / dist) * rFocus));
        line.setAttribute("y1", String(y1 + (dy / dist) * rFocus));
        line.setAttribute("x2", String(x2 - (dx / dist) * rLoupe));
        line.setAttribute("y2", String(y2 - (dy / dist) * rLoupe));
      }
    }

    function applyClarityState(content, stateId) {
      if (!moduleHost || !content || !content.states) return;
      var states = content.states;
      var index = 0;
      var state = states[0];
      var last = states.length - 1;
      var pos;
      var fillPct;
      var i;
      var tickId;
      var diamondKey;
      var loupeKey;
      var diamondImgs;
      var loupeImgs;
      for (i = 0; i < states.length; i++) {
        if (states[i].id === stateId) {
          index = i;
          state = states[i];
          break;
        }
      }

      var visual = moduleHost.querySelector("[data-edu-clarity-visual]");
      var heading = moduleHost.querySelector("[data-edu-clarity-heading]");
      var family = moduleHost.querySelector("[data-edu-clarity-family]");
      var explain = moduleHost.querySelector("[data-edu-clarity-explain]");
      var slider = moduleHost.querySelector("[data-edu-clarity-slider]");
      var bubble = moduleHost.querySelector("[data-edu-clarity-bubble]");
      var ticks = moduleHost.querySelectorAll("[data-edu-clarity-tick]");

      diamondKey = getClarityDiamondKey(state.id);
      loupeKey = getClarityLoupeKey(state.id);
      diamondImgs = moduleHost.querySelectorAll("[data-edu-clarity-img]");
      loupeImgs = moduleHost.querySelectorAll("[data-edu-clarity-loupe-img]");

      activeClarityStateId = state.id;
      pos = clarityAecToTrackPos(index, last);
      fillPct = pos * 100;

      if (visual) visual.setAttribute("data-edu-clarity-grade", state.id);
      for (i = 0; i < diamondImgs.length; i++) {
        if (diamondImgs[i].getAttribute("data-edu-clarity-img") === diamondKey) {
          diamondImgs[i].classList.add("is-active");
        } else {
          diamondImgs[i].classList.remove("is-active");
        }
      }
      for (i = 0; i < loupeImgs.length; i++) {
        if (loupeImgs[i].getAttribute("data-edu-clarity-loupe-img") === loupeKey) {
          loupeImgs[i].classList.add("is-active");
        } else {
          loupeImgs[i].classList.remove("is-active");
        }
      }
      if (heading) heading.textContent = state.heading || (state.label + " Clarity");
      if (family) family.textContent = state.family || "";
      setScaleExplanation(explain, state.explanation);
      if (bubble) bubble.textContent = state.label;
      if (slider) {
        if (slider.style && typeof slider.style.setProperty === "function") {
          slider.style.setProperty("--edu-clarity-n", String(pos));
          slider.style.setProperty("--edu-clarity-track-pct", fillPct + "%");
        }
        applyClarityAecBubbleLayout(slider, pos);
        slider.setAttribute("aria-valuemin", "0");
        slider.setAttribute("aria-valuemax", String(Math.max(0, last)));
        slider.setAttribute("aria-valuenow", String(index));
        slider.setAttribute("aria-valuetext", state.label);
      }
      for (i = 0; i < ticks.length; i++) {
        tickId = ticks[i].getAttribute("data-edu-clarity-tick");
        if (tickId === state.id) ticks[i].setAttribute("aria-current", "true");
        else ticks[i].removeAttribute("aria-current");
      }
      updateClarityOptics(state);
    }

    function renderClarityModule(content) {
      if (!moduleHost) throw new Error("edu-module-host-missing");

      var defaultId = resolveContextStateId(content);
      var groupLabel = "Clarity grades";
      var html = [];
      var i;
      var state;
      var layer;
      var diamondLayers = collectClarityImgLayers("diamond");
      var loupeLayers = collectClarityImgLayers("loupe");
      var defaultDiamondKey = getClarityDiamondKey(defaultId);
      var defaultLoupeKey = getClarityLoupeKey(defaultId);
      var sizerUrl = getClarityMasterUrl();
      var last = content.states.length - 1;
      var defaultIndex = clarityAecStateIndex(content, defaultId);
      var pos = clarityAecToTrackPos(defaultIndex, last);
      var fillPct = pos * 100;
      var tickPos;

      html.push('<div class="diamond-search__edu-clarity" data-edu-clarity>');
      html.push('<div class="diamond-search__edu-clarity-visual" data-edu-clarity-visual aria-hidden="true">');
      html.push('<div class="diamond-search__edu-clarity-stage">');
      html.push('<div class="diamond-search__edu-clarity-media" data-edu-clarity-media>');
      if (sizerUrl) {
        html.push(
          '<img class="diamond-search__edu-clarity-sizer" src="' +
            escapeHtml(sizerUrl) +
            '" width="960" height="564" alt="" decoding="async" aria-hidden="true">'
        );
      }
      for (i = 0; i < diamondLayers.length; i++) {
        layer = diamondLayers[i];
        html.push(
          '<img class="diamond-search__edu-clarity-diamond' +
            (layer.key === defaultDiamondKey ? " is-active" : "") +
            '" data-edu-clarity-img="' +
            escapeHtml(layer.key) +
            '" src="' +
            escapeHtml(layer.src) +
            '" width="' +
            layer.width +
            '" height="' +
            layer.height +
            '" alt="" decoding="async">'
        );
      }
      html.push('<svg class="diamond-search__edu-clarity-chars" viewBox="0 0 960 564" preserveAspectRatio="xMidYMid meet" focusable="false">');
      html.push('<circle class="diamond-search__edu-clarity-focus" data-edu-clarity-focus cx="556" cy="336" r="20" fill="none" stroke="#b8b3ab" stroke-width="2.25"></circle>');
      html.push("</svg></div>");

      html.push('<div class="diamond-search__edu-clarity-inspect">');
      html.push('<div class="diamond-search__edu-clarity-loupe" data-edu-clarity-loupe>');
      for (i = 0; i < loupeLayers.length; i++) {
        layer = loupeLayers[i];
        html.push(
          '<img class="diamond-search__edu-clarity-loupe-view' +
            (layer.key === defaultLoupeKey ? " is-active" : "") +
            '" data-edu-clarity-loupe-img="' +
            escapeHtml(layer.key) +
            '" src="' +
            escapeHtml(layer.src) +
            '" width="' +
            layer.width +
            '" height="' +
            layer.height +
            '" alt="" decoding="async">'
        );
      }
      html.push("</div>");
      html.push('<span class="diamond-search__edu-clarity-loupe-label">10×</span>');
      html.push("</div>");

      html.push('<svg class="diamond-search__edu-clarity-leader" data-edu-clarity-leader focusable="false">');
      html.push('<line data-edu-clarity-leader-line x1="0" y1="0" x2="0" y2="0"></line>');
      html.push("</svg>");
      html.push("</div></div>");

      html.push('<div class="diamond-search__edu-clarity-info">');
      html.push('<p class="diamond-search__edu-clarity-heading" data-edu-clarity-heading></p>');
      html.push('<p class="diamond-search__edu-clarity-family" data-edu-clarity-family></p>');
      html.push("</div>");

      html.push('<div class="diamond-search__edu-clarity-scale">');
      html.push(
        '<div class="diamond-search__edu-clarity-slider" data-edu-clarity-slider role="slider" tabindex="0" aria-label="' +
          groupLabel +
          '" aria-valuemin="0" aria-valuemax="' +
          last +
          '" aria-valuenow="' +
          defaultIndex +
          '" aria-valuetext="' +
          escapeHtml(content.states[defaultIndex].label) +
          '" style="--edu-clarity-n:' +
          pos +
          ';--edu-clarity-track-pct:' +
          fillPct +
          '%;--edu-clarity-bubble-pointer-shift:0px">'
      );
      html.push(
        '<div class="diamond-search__edu-clarity-bubble" data-edu-clarity-bubble aria-hidden="true">' +
          escapeHtml(content.states[defaultIndex].label) +
          "</div>"
      );
      html.push('<div class="diamond-search__edu-clarity-track" data-edu-clarity-track>');
      html.push('<span class="diamond-search__edu-clarity-rail" aria-hidden="true"></span>');
      html.push('<span class="diamond-search__edu-clarity-fill" data-edu-clarity-fill aria-hidden="true"></span>');
      html.push('<span class="diamond-search__edu-clarity-handle" data-edu-clarity-handle aria-hidden="true"></span>');
      html.push("</div>");
      html.push('<div class="diamond-search__edu-clarity-ticks" role="presentation">');
      for (i = 0; i < content.states.length; i++) {
        state = content.states[i];
        tickPos = last <= 0 ? 0 : i / last;
        html.push(
          '<button type="button" class="diamond-search__edu-clarity-tick" data-edu-clarity-tick="' +
            escapeHtml(state.id) +
            '"' +
            (state.id === defaultId ? ' aria-current="true"' : "") +
            ' tabindex="-1" style="--edu-clarity-tick-n:' +
            tickPos +
            '" aria-label="Set clarity grade to ' +
            escapeHtml(state.label) +
            '"><span class="diamond-search__edu-clarity-tick-mark" aria-hidden="true"></span><span class="diamond-search__edu-clarity-tick-label">' +
            escapeHtml(state.label) +
            "</span></button>"
        );
      }
      html.push("</div></div></div>");
      html.push(
        '<p class="diamond-search__edu-scale-explain" id="diamondSearchEduClarityExplain" data-edu-clarity-explain aria-live="polite"></p>'
      );
      html.push("</div>");

      moduleHost.innerHTML = html.join("");
      moduleHost.hidden = false;
      panel.classList.add("diamond-search__edu-panel--module");
      activeScaleContent = content;
      applyClarityState(content, defaultId);
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(function () {
          if (activeScaleContent === content) applyClarityState(content, defaultId);
        });
      }

      var masterEl = moduleHost.querySelector(".diamond-search__edu-clarity-diamond.is-active");
      if (masterEl && !masterEl.complete) {
        masterEl.addEventListener("load", function onClarityMasterLoad() {
          masterEl.removeEventListener("load", onClarityMasterLoad);
          if (activeScaleContent === content) applyClarityState(content, defaultId);
        });
      }
    }

    /*
     * Carat AEC calibration — one coordinate system, delegated to
     * window.IvanovCaratAec (assets/diamond-search-carat-aec.js).
     *
     * Native hand asset: 859 × 1024.
     * Ring-finger width at the placement row y=428, crease to crease: 90 px.
     * US ring size 5.5 inner diameter: 15.90 mm.
     * Shared diamond anchor: (325, 428) — Lightest-hand ring-finger
     * centerline at y=428 (span 283–367). Do not invent per-tone offsets.
     *
     * Search pipeline:
     *   Round: slider carat → typical diameter_mm → native px/mm → CSS %
     *   Oval/Pear/Marquise/Cushion/Princess: slider carat → typical
     *   lengthMm × widthMm → same px/mm
     * CSS sizes the overlay as a fraction of the hand so resizing the
     * hand recalibrates automatically. Center stays on the approved
     * ring-finger seat. No per-state CSS scale. Pear adds only an
     * asset-internal 180° rotation so the source point-up master
     * displays point-down. Implemented fancy shapes: Oval, Pear,
     * Marquise, Cushion, Princess.
     */
    var caratAec = window.IvanovCaratAec || null;
    var CARAT_HAND_NATIVE_WIDTH = caratAec ? caratAec.HAND_NATIVE_WIDTH : 859;
    var CARAT_NATIVE_PX_PER_MM = caratAec ? caratAec.NATIVE_PX_PER_MM : 90 / 15.9;
    var CARAT_ANCHOR_X = caratAec ? caratAec.ANCHOR_X : 325 / 859;
    var CARAT_ANCHOR_Y = caratAec ? caratAec.ANCHOR_Y : 428 / 1024;
    var activeCaratValue = caratAec ? caratAec.DEFAULT_CARAT : 1;

    function requireCaratAec() {
      caratAec = window.IvanovCaratAec || caratAec;
      if (!caratAec) throw new Error("ivanov-carat-aec-missing");
      return caratAec;
    }

    function resolveContextCarat() {
      var raw = pendingEduContext && pendingEduContext.carat;
      var n = parseFloat(raw);
      if (Number.isFinite(n)) return requireCaratAec().snapCarat(n);
      return requireCaratAec().DEFAULT_CARAT;
    }

    function caratDiameterMm(ct) {
      return requireCaratAec().resolveSearchDiameterMm(ct);
    }

    function getCaratToneUrl(tone) {
      if (!moduleHost) return "";
      return String(moduleHost.getAttribute("data-edu-carat-hand-tone-" + tone) || "").trim();
    }

    function isUsableCaratToneUrl(url) {
      var value = url == null ? "" : String(url).trim();
      if (!value) return false;
      if (/^javascript:/i.test(value)) return false;
      return true;
    }

    function caratTonePreloadStatus(url) {
      var entry = isUsableCaratToneUrl(url) ? caratTonePreloadMap[String(url).trim()] : null;
      return entry && entry.status ? entry.status : "";
    }

    function caratToneImageCtor() {
      if (window && typeof window.Image === "function") return window.Image;
      return null;
    }

    function decodeCaratToneUrl(url, cb) {
      var value = isUsableCaratToneUrl(url) ? String(url).trim() : "";
      var entry;
      var ImageCtor;
      var img;
      var finished = false;

      function done(ok) {
        var waiters;
        var i;
        if (finished) return;
        finished = true;
        entry.status = ok ? "ok" : "fail";
        waiters = entry.waiters || [];
        entry.waiters = [];
        for (i = 0; i < waiters.length; i += 1) waiters[i](ok);
      }

      if (!value) {
        if (cb) cb(false);
        return;
      }
      entry = caratTonePreloadMap[value];
      if (entry && entry.status === "ok") {
        if (cb) cb(true);
        return;
      }
      if (entry && entry.status === "fail") {
        if (cb) cb(false);
        return;
      }
      if (entry && entry.status === "pending") {
        if (cb) entry.waiters.push(cb);
        return;
      }
      entry = { status: "pending", waiters: [] };
      caratTonePreloadMap[value] = entry;
      if (cb) entry.waiters.push(cb);
      ImageCtor = caratToneImageCtor();
      if (!ImageCtor) {
        done(true);
        return;
      }
      img = new ImageCtor();
      entry.img = img;
      img.onload = function () {
        if (typeof img.decode === "function") {
          img.decode().then(
            function () {
              done(true);
            },
            function () {
              done(true);
            }
          );
          return;
        }
        done(true);
      };
      img.onerror = function () {
        done(false);
      };
      img.src = value;
      if (img.complete && img.naturalWidth > 0) done(true);
    }

    function preloadCaratToneAssets() {
      var i;
      var url;
      for (i = 1; i <= caratAec.SKIN_TONE_COUNT; i++) {
        url = getCaratToneUrl(i);
        if (isUsableCaratToneUrl(url)) decodeCaratToneUrl(url);
      }
    }

    function caratToneElementUrl(el) {
      if (!el) return "";
      return String(el.getAttribute("src") || el.src || "").trim();
    }

    function hideCaratToneFade(fade) {
      if (!fade) return;
      if (fade.classList && typeof fade.classList.remove === "function") {
        fade.classList.remove("is-visible");
      }
    }

    function applyCaratToneHand(base, fade, nextUrl) {
      var currentUrl;
      var fadeUrl;
      var gen;
      if (!base) return;
      currentUrl = caratToneElementUrl(base);
      fadeUrl = caratToneElementUrl(fade);
      if (fade && !isUsableCaratToneUrl(fadeUrl) && isUsableCaratToneUrl(currentUrl)) {
        fade.setAttribute("src", currentUrl);
      }
      hideCaratToneFade(fade);
      if (!isUsableCaratToneUrl(nextUrl)) return;
      if (nextUrl === currentUrl) return;
      caratToneFadeGen += 1;
      gen = caratToneFadeGen;
      decodeCaratToneUrl(nextUrl, function (ok) {
        if (gen !== caratToneFadeGen) return;
        if (!ok) return;
        base.setAttribute("src", nextUrl);
        if (fade) fade.setAttribute("src", nextUrl);
        hideCaratToneFade(fade);
      });
    }

    function getCaratDefaultTone() {
      var raw = moduleHost && moduleHost.getAttribute("data-edu-carat-hand-tone-default");
      var n = parseInt(raw, 10);
      if (n >= 1 && n <= caratAec.SKIN_TONE_COUNT) return n;
      return caratAec.DEFAULT_SKIN_TONE;
    }

    function countCaratToneAssets() {
      var count = 0;
      var i;
      for (i = 1; i <= caratAec.SKIN_TONE_COUNT; i++) {
        if (isUsableCaratToneUrl(getCaratToneUrl(i))) count += 1;
      }
      return count;
    }

    /* Mobile Skin Tone endpoints only. Tone 2–4 stay on the linear
       25/50/75 stops. Desktop stays 0/25/50/75/100. 8px inset puts the
       16px thumb fully inside the 72px track so LIGHTER / DARKER keep a
       clear outer-edge gap after the mobile label gap. */
    var CARAT_TONE_MOBILE_INSET_PX = 8;
    function isMobileCaratToneLayout() {
      return window.matchMedia && window.matchMedia("(max-width: 989px)").matches;
    }
    function caratTonePercent(tone, sliderEl) {
      var count = caratAec.SKIN_TONE_COUNT;
      var linear = ((tone - 1) / (count - 1)) * 100;
      if ((tone !== 1 && tone !== 5) || !isMobileCaratToneLayout()) return linear;
      var h = (sliderEl && sliderEl.clientHeight) || 72;
      var insetPct = (CARAT_TONE_MOBILE_INSET_PX / h) * 100;
      return tone === 1 ? insetPct : 100 - insetPct;
    }
    function syncCaratToneTicks(sliderEl) {
      var ticks = moduleHost && moduleHost.querySelectorAll(".diamond-search__edu-carat-tone-tick");
      var i;
      if (!ticks || !ticks.length) return;
      for (i = 0; i < ticks.length; i++) {
        ticks[i].style.top = caratTonePercent(i + 1, sliderEl) + "%";
      }
    }

    function applyCaratTone(tone) {
      var base = moduleHost && moduleHost.querySelector("[data-edu-carat-hand-layer='base']");
      var fade = moduleHost && moduleHost.querySelector("[data-edu-carat-hand-layer='fade']");
      var control = moduleHost && moduleHost.querySelector("[data-edu-carat-tone-slider]");
      var next;
      var nextUrl;
      var enabled = countCaratToneAssets() >= 2;

      next = Math.round(Number(tone));
      if (!isFinite(next)) next = getCaratDefaultTone();
      next = Math.max(1, Math.min(caratAec.SKIN_TONE_COUNT, next));
      nextUrl = getCaratToneUrl(next);

      if (control) {
        control.setAttribute("aria-valuenow", String(next));
        control.setAttribute(
          "aria-valuetext",
          enabled ? next + " of " + caratAec.SKIN_TONE_COUNT : "Default hand"
        );
        control.setAttribute("data-edu-carat-tone-value", String(next));
        control.style.setProperty(
          "--edu-carat-tone-pct",
          caratTonePercent(next, control) + "%"
        );
        syncCaratToneTicks(control);
        if (enabled) {
          control.removeAttribute("aria-disabled");
          control.setAttribute("tabindex", "0");
        } else {
          control.setAttribute("aria-disabled", "true");
          control.setAttribute("tabindex", "-1");
        }
      }

      applyCaratToneHand(base, fade, nextUrl);
    }

    function applyCaratOverlay(size) {
      var diamond = moduleHost && moduleHost.querySelector(".diamond-search__edu-carat-diamond");
      var root = moduleHost && moduleHost.querySelector("[data-edu-carat]");
      var url = getCaratDiamondUrl(size && size.shape);
      if (root) root.setAttribute("data-edu-carat-shape", size && size.shape ? size.shape : "round");
      if (!diamond || !size) return;
      diamond.setAttribute("data-edu-carat-shape", size.shape);
      if (size.shape === "oval") diamond.classList.add("diamond-search__edu-carat-diamond--oval");
      else diamond.classList.remove("diamond-search__edu-carat-diamond--oval");
      if (size.shape === "pear") diamond.classList.add("diamond-search__edu-carat-diamond--pear");
      else diamond.classList.remove("diamond-search__edu-carat-diamond--pear");
      if (size.shape === "marquise") diamond.classList.add("diamond-search__edu-carat-diamond--marquise");
      else diamond.classList.remove("diamond-search__edu-carat-diamond--marquise");
      if (size.shape === "cushion") diamond.classList.add("diamond-search__edu-carat-diamond--cushion");
      else diamond.classList.remove("diamond-search__edu-carat-diamond--cushion");
      if (size.shape === "princess") diamond.classList.add("diamond-search__edu-carat-diamond--princess");
      else diamond.classList.remove("diamond-search__edu-carat-diamond--princess");
      if (size.shape === "emerald") diamond.classList.add("diamond-search__edu-carat-diamond--emerald");
      else diamond.classList.remove("diamond-search__edu-carat-diamond--emerald");
      if (size.shape === "radiant") diamond.classList.add("diamond-search__edu-carat-diamond--radiant");
      else diamond.classList.remove("diamond-search__edu-carat-diamond--radiant");
      if (size.shape === "heart") diamond.classList.add("diamond-search__edu-carat-diamond--heart");
      else diamond.classList.remove("diamond-search__edu-carat-diamond--heart");
      if (size.shape === "asscher") diamond.classList.add("diamond-search__edu-carat-diamond--asscher");
      else diamond.classList.remove("diamond-search__edu-carat-diamond--asscher");
      if (url && diamond.getAttribute("src") !== url) diamond.setAttribute("src", url);
    }

    function applyCaratValue(content, carat) {
      if (!moduleHost || !content) return;
      var ct = caratAec.snapCarat(carat);
      var shape = getActiveSearchShape();
      var size = caratAec.resolveSearchSize(ct, shape);
      var mm = size.kind === "lw" ? null : size.diameterMm;
      var pos = caratAec.caratToTrackPos(ct);
      var fillPct = pos * 100;
      var media = moduleHost.querySelector("[data-edu-carat-media]");
      var heading = moduleHost.querySelector("[data-edu-carat-heading]");
      var diameter = moduleHost.querySelector("[data-edu-carat-diameter]");
      var bubble = moduleHost.querySelector("[data-edu-carat-bubble]");
      var explain = moduleHost.querySelector("[data-edu-carat-explain]");
      var slider = moduleHost.querySelector("[data-edu-carat-slider]");
      var ticks = moduleHost.querySelectorAll("[data-edu-carat-tick]");
      var root = moduleHost.querySelector("[data-edu-carat]");
      var i;
      var tickCt;

      activeCaratValue = ct;
      if (root) root.setAttribute("data-edu-carat-value", ct.toFixed(2));
      applyCaratOverlay(size);
      if (media) {
        if (size.kind === "lw") {
          media.style.setProperty("--edu-carat-length-mm", String(size.lengthMm));
          media.style.setProperty("--edu-carat-width-mm", String(size.widthMm));
          media.style.setProperty("--edu-carat-shadow-frac", String(size.widthMm * CARAT_NATIVE_PX_PER_MM / CARAT_HAND_NATIVE_WIDTH));
        } else {
          media.style.setProperty("--edu-carat-diameter-mm", String(mm));
          media.style.setProperty("--edu-carat-shadow-frac", String(mm * CARAT_NATIVE_PX_PER_MM / CARAT_HAND_NATIVE_WIDTH));
        }
        media.style.setProperty("--edu-carat-track-pct", fillPct + "%");
      }
      if (slider) {
        slider.style.setProperty("--edu-carat-track-pct", fillPct + "%");
        applyCaratBubbleLayout(slider, pos);
        slider.setAttribute("aria-valuenow", String(ct));
        slider.setAttribute("aria-valuetext", caratAec.formatCaratLabel(ct));
      }
      if (heading) heading.textContent = caratAec.formatCaratLabel(ct);
      if (diameter) {
        diameter.textContent =
          size.kind === "lw"
            ? caratAec.formatTypicalSize(size.lengthMm, size.widthMm)
            : caratAec.formatTypicalDiameter(mm);
      }
      if (bubble) bubble.textContent = caratAec.formatCaratLabel(ct);
      setScaleExplanation(explain, content.explanation || "");
      for (i = 0; i < ticks.length; i++) {
        tickCt = parseFloat(ticks[i].getAttribute("data-edu-carat-tick"));
        if (tickCt === ct) ticks[i].setAttribute("aria-current", "true");
        else ticks[i].removeAttribute("aria-current");
      }
    }

    function applyCaratState(content, stateId) {
      if (!content || !content.states) return;
      var i;
      var state = content.states[0];
      for (i = 0; i < content.states.length; i++) {
        if (content.states[i].id === stateId) {
          state = content.states[i];
          break;
        }
      }
      applyCaratValue(content, typeof state.carat === "number" ? state.carat : caratAec.DEFAULT_CARAT);
    }

    function applyCaratBubbleLayout(slider, pos) {
      var layout;
      if (!slider || !caratAec.clampBubbleLayout) return;
      layout = caratAec.clampBubbleLayout(pos, slider.clientWidth, caratAec.BUBBLE_HALF_PX);
      if (layout.leftPx != null) {
        slider.style.setProperty("--edu-carat-bubble-left", layout.leftPx + "px");
      } else {
        slider.style.setProperty("--edu-carat-bubble-left", layout.leftPct + "%");
      }
      slider.style.setProperty("--edu-carat-bubble-pointer-shift", layout.pointerShiftPx + "px");
    }

    function caratPosFromClientX(slider, clientX) {
      var track = slider.querySelector("[data-edu-carat-track]");
      var rect = (track || slider).getBoundingClientRect();
      if (!rect.width) return caratAec.caratToTrackPos(activeCaratValue);
      return (clientX - rect.left) / rect.width;
    }

    function onCaratPointerDown(e) {
      var slider = e.target && e.target.closest && e.target.closest("[data-edu-carat-slider]");
      if (!slider || !activeScaleContent || !isCaratScaleContent(activeScaleContent)) return;
      if (e.target.closest && e.target.closest("[data-edu-carat-tick]")) return;
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      caratPointerBound = slider;
      if (slider.setPointerCapture && e.pointerId != null) {
        try {
          slider.setPointerCapture(e.pointerId);
        } catch (err) {}
      }
      applyCaratValue(activeScaleContent, caratAec.trackPosToCarat(caratPosFromClientX(slider, e.clientX)));
      focusNoScroll(slider);
    }

    function onCaratPointerMove(e) {
      if (caratTonePointerBound) {
        applyCaratTone(toneFromClientY(caratTonePointerBound, e.clientY));
        return;
      }
      if (!caratPointerBound || !activeScaleContent) return;
      applyCaratValue(
        activeScaleContent,
        caratAec.trackPosToCarat(caratPosFromClientX(caratPointerBound, e.clientX))
      );
    }

    function onCaratPointerUp() {
      caratPointerBound = null;
      caratTonePointerBound = null;
    }

    function onCaratSliderKeydown(e) {
      if (!activeScaleContent || !isCaratScaleContent(activeScaleContent)) return;
      if (!e.target || !e.target.closest || !e.target.closest("[data-edu-carat-slider]")) return;
      var next = activeCaratValue;
      if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = activeCaratValue - caratAec.CARAT_STEP;
      else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = activeCaratValue + caratAec.CARAT_STEP;
      else if (e.key === "PageDown") next = activeCaratValue - 0.25;
      else if (e.key === "PageUp") next = activeCaratValue + 0.25;
      else if (e.key === "Home") next = caratAec.CARAT_MIN;
      else if (e.key === "End") next = caratAec.CARAT_MAX;
      else return;
      e.preventDefault();
      applyCaratValue(activeScaleContent, next);
    }

    function onCaratTickActivate(e) {
      var tick = e.target && e.target.closest && e.target.closest("[data-edu-carat-tick]");
      var ct;
      if (!tick || !activeScaleContent || !isCaratScaleContent(activeScaleContent)) return;
      ct = parseFloat(tick.getAttribute("data-edu-carat-tick"));
      if (!isFinite(ct)) return;
      e.preventDefault();
      applyCaratValue(activeScaleContent, ct);
    }

    function toneFromClientY(control, clientY) {
      var rect = control.getBoundingClientRect();
      var t;
      if (!rect.height) return getCaratDefaultTone();
      t = (clientY - rect.top) / rect.height;
      return Math.max(1, Math.min(caratAec.SKIN_TONE_COUNT, Math.round(t * (caratAec.SKIN_TONE_COUNT - 1)) + 1));
    }

    function onCaratTonePointerDown(e) {
      var control = e.target && e.target.closest && e.target.closest("[data-edu-carat-tone-slider]");
      if (!control || control.getAttribute("aria-disabled") === "true") return;
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      caratTonePointerBound = control;
      if (control.setPointerCapture && e.pointerId != null) {
        try {
          control.setPointerCapture(e.pointerId);
        } catch (err) {}
      }
      applyCaratTone(toneFromClientY(control, e.clientY));
      focusNoScroll(control);
    }

    function onCaratToneKeydown(e) {
      var control = e.target && e.target.closest && e.target.closest("[data-edu-carat-tone-slider]");
      var current;
      var next;
      if (!control || control.getAttribute("aria-disabled") === "true") return;
      current = parseInt(control.getAttribute("data-edu-carat-tone-value") || "", 10) || getCaratDefaultTone();
      if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = current - 1;
      else if (e.key === "ArrowDown" || e.key === "ArrowRight") next = current + 1;
      else if (e.key === "Home") next = 1;
      else if (e.key === "End") next = caratAec.SKIN_TONE_COUNT;
      else return;
      e.preventDefault();
      next = Math.max(1, Math.min(caratAec.SKIN_TONE_COUNT, next));
      applyCaratTone(next);
    }

    function onCutPointerDown(e) {
      var slider = e.target && e.target.closest && e.target.closest("[data-edu-cut-slider]");
      if (!slider || !activeScaleContent || !isCutScaleContent(activeScaleContent)) return;
      if (e.target.closest && e.target.closest("[data-edu-cut-tick]")) return;
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      cutPointerBound = slider;
      if (slider.setPointerCapture && e.pointerId != null) {
        try {
          slider.setPointerCapture(e.pointerId);
        } catch (err) {}
      }
      applyScaleState(
        activeScaleContent,
        cutAecFromTrackPos(cutAecPosFromClientX(slider, e.clientX, activeCutStateId, activeScaleContent), activeScaleContent)
      );
      focusNoScroll(slider);
    }

    function onCutPointerMove(e) {
      if (!cutPointerBound || !activeScaleContent || !isCutScaleContent(activeScaleContent)) return;
      applyScaleState(
        activeScaleContent,
        cutAecFromTrackPos(
          cutAecPosFromClientX(cutPointerBound, e.clientX, activeCutStateId, activeScaleContent),
          activeScaleContent
        )
      );
    }

    function onCutPointerUp() {
      cutPointerBound = null;
    }

    function onCutSliderKeydown(e) {
      var states;
      var next;
      if (!activeScaleContent || !isCutScaleContent(activeScaleContent)) return;
      if (!e.target || !e.target.closest || !e.target.closest("[data-edu-cut-slider]")) return;
      states = activeScaleContent.states || [];
      if (!states.length) return;
      if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = stepCutAecState(activeScaleContent, activeCutStateId, -1);
      else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = stepCutAecState(activeScaleContent, activeCutStateId, 1);
      else if (e.key === "Home") next = states[0].id;
      else if (e.key === "End") next = states[states.length - 1].id;
      else return;
      e.preventDefault();
      applyScaleState(activeScaleContent, next);
    }

    function onCutTickActivate(e) {
      var tick = e.target && e.target.closest && e.target.closest("[data-edu-cut-tick]");
      var id;
      if (!tick || !activeScaleContent || !isCutScaleContent(activeScaleContent)) return;
      id = tick.getAttribute("data-edu-cut-tick");
      if (!id) return;
      e.preventDefault();
      applyScaleState(activeScaleContent, id);
    }

    function onColorPointerDown(e) {
      var slider = e.target && e.target.closest && e.target.closest("[data-edu-color-slider]");
      if (!slider || !activeScaleContent || !isColorScaleContent(activeScaleContent)) return;
      if (e.target.closest && e.target.closest("[data-edu-color-tick]")) return;
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      colorPointerBound = slider;
      if (slider.setPointerCapture && e.pointerId != null) {
        try {
          slider.setPointerCapture(e.pointerId);
        } catch (err) {}
      }
      applyColorState(
        activeScaleContent,
        colorAecFromTrackPos(colorAecPosFromClientX(slider, e.clientX, activeColorStateId, activeScaleContent), activeScaleContent)
      );
      focusNoScroll(slider);
    }

    function onColorPointerMove(e) {
      if (!colorPointerBound || !activeScaleContent || !isColorScaleContent(activeScaleContent)) return;
      applyColorState(
        activeScaleContent,
        colorAecFromTrackPos(
          colorAecPosFromClientX(colorPointerBound, e.clientX, activeColorStateId, activeScaleContent),
          activeScaleContent
        )
      );
    }

    function onColorPointerUp() {
      colorPointerBound = null;
    }

    function onColorSliderKeydown(e) {
      var states;
      var next;
      if (!activeScaleContent || !isColorScaleContent(activeScaleContent)) return;
      if (!e.target || !e.target.closest || !e.target.closest("[data-edu-color-slider]")) return;
      states = activeScaleContent.states || [];
      if (!states.length) return;
      if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = stepColorAecState(activeScaleContent, activeColorStateId, -1);
      else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = stepColorAecState(activeScaleContent, activeColorStateId, 1);
      else if (e.key === "Home") next = states[0].id;
      else if (e.key === "End") next = states[states.length - 1].id;
      else return;
      e.preventDefault();
      applyColorState(activeScaleContent, next);
    }

    function onColorTickActivate(e) {
      var tick = e.target && e.target.closest && e.target.closest("[data-edu-color-tick]");
      var id;
      if (!tick || !activeScaleContent || !isColorScaleContent(activeScaleContent)) return;
      id = tick.getAttribute("data-edu-color-tick");
      if (!id) return;
      e.preventDefault();
      applyColorState(activeScaleContent, id);
    }

    function onClarityPointerDown(e) {
      var slider = e.target && e.target.closest && e.target.closest("[data-edu-clarity-slider]");
      if (!slider || !activeScaleContent || !isClarityScaleContent(activeScaleContent)) return;
      if (e.target.closest && e.target.closest("[data-edu-clarity-tick]")) return;
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      clarityPointerBound = slider;
      if (slider.setPointerCapture && e.pointerId != null) {
        try {
          slider.setPointerCapture(e.pointerId);
        } catch (err) {}
      }
      applyClarityState(
        activeScaleContent,
        clarityAecFromTrackPos(clarityAecPosFromClientX(slider, e.clientX, activeClarityStateId, activeScaleContent), activeScaleContent)
      );
      focusNoScroll(slider);
    }

    function onClarityPointerMove(e) {
      if (!clarityPointerBound || !activeScaleContent || !isClarityScaleContent(activeScaleContent)) return;
      applyClarityState(
        activeScaleContent,
        clarityAecFromTrackPos(
          clarityAecPosFromClientX(clarityPointerBound, e.clientX, activeClarityStateId, activeScaleContent),
          activeScaleContent
        )
      );
    }

    function onClarityPointerUp() {
      clarityPointerBound = null;
    }

    function onClaritySliderKeydown(e) {
      var states;
      var next;
      if (!activeScaleContent || !isClarityScaleContent(activeScaleContent)) return;
      if (!e.target || !e.target.closest || !e.target.closest("[data-edu-clarity-slider]")) return;
      states = activeScaleContent.states || [];
      if (!states.length) return;
      if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = stepClarityAecState(activeScaleContent, activeClarityStateId, -1);
      else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = stepClarityAecState(activeScaleContent, activeClarityStateId, 1);
      else if (e.key === "Home") next = states[0].id;
      else if (e.key === "End") next = states[states.length - 1].id;
      else return;
      e.preventDefault();
      applyClarityState(activeScaleContent, next);
    }

    function onClarityTickActivate(e) {
      var tick = e.target && e.target.closest && e.target.closest("[data-edu-clarity-tick]");
      var id;
      if (!tick || !activeScaleContent || !isClarityScaleContent(activeScaleContent)) return;
      id = tick.getAttribute("data-edu-clarity-tick");
      if (!id) return;
      e.preventDefault();
      applyClarityState(activeScaleContent, id);
    }

    function renderCaratModule(content) {
      if (!moduleHost) throw new Error("edu-module-host-missing");
      requireCaratAec();

      var defaultCarat = resolveContextCarat();
      var html = [];
      var i;
      var tick;
      var tickLabel;
      var defaultTone = getCaratDefaultTone();
      var shape = getActiveSearchShape();
      var size = caratAec.resolveSearchSize(defaultCarat, shape);
      var handUrl = getCaratToneUrl(defaultTone) || getCaratHandUrl();
      var diamondUrl = getCaratDiamondUrl(shape);
      var diamondClass = "diamond-search__edu-carat-diamond";
      var toneEnabled = countCaratToneAssets() >= 2;
      var mediaStyle =
        "--edu-carat-hand-native-w:" +
        CARAT_HAND_NATIVE_WIDTH +
        ";--edu-carat-native-px-per-mm:" +
        CARAT_NATIVE_PX_PER_MM +
        ";--edu-carat-anchor-x:" +
        CARAT_ANCHOR_X +
        ";--edu-carat-anchor-y:" +
        CARAT_ANCHOR_Y +
        ";--edu-carat-diameter-mm:" +
        caratDiameterMm(defaultCarat) +
        ";--edu-carat-track-pct:" +
        caratAec.caratToTrackPos(defaultCarat) * 100 +
        "%" +
        ";--edu-carat-shadow-frac:" +
        (size.kind === "lw" ? size.widthMm : caratDiameterMm(defaultCarat)) * CARAT_NATIVE_PX_PER_MM / CARAT_HAND_NATIVE_WIDTH;
      if (size.kind === "lw") {
        if (shape === "oval") diamondClass += " diamond-search__edu-carat-diamond--oval";
        if (shape === "pear") diamondClass += " diamond-search__edu-carat-diamond--pear";
        if (shape === "marquise") diamondClass += " diamond-search__edu-carat-diamond--marquise";
        if (shape === "cushion") diamondClass += " diamond-search__edu-carat-diamond--cushion";
        if (shape === "princess") diamondClass += " diamond-search__edu-carat-diamond--princess";
        if (shape === "emerald") diamondClass += " diamond-search__edu-carat-diamond--emerald";
        if (shape === "radiant") diamondClass += " diamond-search__edu-carat-diamond--radiant";
        if (shape === "heart") diamondClass += " diamond-search__edu-carat-diamond--heart";
        if (shape === "asscher") diamondClass += " diamond-search__edu-carat-diamond--asscher";
        mediaStyle +=
          ";--edu-carat-length-mm:" +
          size.lengthMm +
          ";--edu-carat-width-mm:" +
          size.widthMm;
      }

      html.push(
        '<div class="diamond-search__edu-carat" data-edu-carat data-edu-carat-value="' +
          defaultCarat.toFixed(2) +
          '" data-edu-carat-shape="' +
          shape +
          '">'
      );
      html.push('<div class="diamond-search__edu-carat-visual">');
      html.push('<div class="diamond-search__edu-carat-stage">');
      html.push(
        '<div class="diamond-search__edu-carat-media" data-edu-carat-media style="' +
          mediaStyle +
          '">'
      );
      html.push('<div class="diamond-search__edu-carat-hands" aria-hidden="true">');
      if (handUrl) {
        html.push(
          '<img class="diamond-search__edu-carat-hand" data-edu-carat-hand-layer="base" src="' +
            escapeHtml(handUrl) +
            '" width="859" height="1024" alt="" decoding="async">'
        );
        html.push(
          '<img class="diamond-search__edu-carat-hand diamond-search__edu-carat-hand--fade" data-edu-carat-hand-layer="fade" src="' +
            escapeHtml(handUrl) +
            '" width="859" height="1024" alt="" decoding="async">'
        );
      }
      html.push("</div>");
      /* Contact shadow: sits between hand layers and diamond overlay */
      html.push('<span class="diamond-search__edu-carat-contact-shadow" aria-hidden="true"></span>');
      if (diamondUrl) {
        html.push(
          '<img class="' +
            diamondClass +
            '" src="' +
            escapeHtml(diamondUrl) +
            '" width="780" height="780" alt="" decoding="async" data-edu-carat-shape="' +
            shape +
            '">'
        );
      }
      html.push("</div>");
      html.push('<div class="diamond-search__edu-carat-tone" data-edu-carat-tone>');
      html.push('<span class="diamond-search__edu-carat-tone-label" aria-hidden="true">Lighter</span>');
      html.push(
        '<div class="diamond-search__edu-carat-tone-slider" data-edu-carat-tone-slider role="slider" aria-label="Skin tone" aria-valuemin="1" aria-valuemax="' +
          caratAec.SKIN_TONE_COUNT +
          '" aria-valuenow="' +
          defaultTone +
          '" aria-orientation="vertical" data-edu-carat-tone-value="' +
          defaultTone +
          '"' +
          (toneEnabled ? ' tabindex="0"' : ' tabindex="-1" aria-disabled="true"') +
          ">"
      );
      html.push('<span class="diamond-search__edu-carat-tone-rail" aria-hidden="true"></span>');
      for (var tki = 1; tki <= caratAec.SKIN_TONE_COUNT; tki++) {
        var tkPct = caratTonePercent(tki);
        html.push('<span class="diamond-search__edu-carat-tone-tick" style="top:' + tkPct + '%"></span>');
      }
      html.push('<span class="diamond-search__edu-carat-tone-thumb" aria-hidden="true"></span>');
      html.push("</div>");
      html.push('<span class="diamond-search__edu-carat-tone-label" aria-hidden="true">Darker</span>');
      html.push("</div>");
      html.push("</div></div>");

      html.push('<div class="diamond-search__edu-carat-info">');
      html.push(
        '<p class="diamond-search__edu-carat-caption">Approximate Size on a Size 5.5 Finger</p>'
      );
      html.push('<p class="diamond-search__edu-carat-heading" data-edu-carat-heading></p>');
      html.push('<p class="diamond-search__edu-carat-diameter" data-edu-carat-diameter></p>');
      html.push(
        '<p class="diamond-search__edu-carat-vary">' +
          escapeHtml(content.varyNote || "Actual dimensions vary by diamond.") +
          "</p>"
      );
      html.push("</div>");

      html.push('<div class="diamond-search__edu-carat-scale">');
      html.push('<p class="diamond-search__edu-carat-slider-label" id="diamondSearchEduCaratWeightLabel">Carat Weight</p>');
      html.push(
        '<div class="diamond-search__edu-carat-slider" data-edu-carat-slider role="slider" tabindex="0" aria-labelledby="diamondSearchEduCaratWeightLabel" aria-valuemin="' +
          caratAec.CARAT_MIN +
          '" aria-valuemax="' +
          caratAec.CARAT_MAX +
          '" aria-valuenow="' +
          defaultCarat +
          '" aria-valuetext="' +
          escapeHtml(caratAec.formatCaratLabel(defaultCarat)) +
          '">'
      );
      html.push('<div class="diamond-search__edu-carat-bubble" data-edu-carat-bubble aria-hidden="true"></div>');
      html.push('<div class="diamond-search__edu-carat-track" data-edu-carat-track>');
      html.push('<span class="diamond-search__edu-carat-rail" aria-hidden="true"></span>');
      html.push('<span class="diamond-search__edu-carat-fill" data-edu-carat-fill aria-hidden="true"></span>');
      html.push('<span class="diamond-search__edu-carat-handle" data-edu-carat-handle aria-hidden="true"></span>');
      html.push("</div>");
      html.push('<div class="diamond-search__edu-carat-ticks" role="presentation">');
      for (i = 0; i < caratAec.TRACK_TICKS.length; i++) {
        tick = caratAec.TRACK_TICKS[i];
        tickLabel = caratAec.formatTrackTickLabel(tick.ct);
        html.push(
          '<button type="button" class="diamond-search__edu-carat-tick" data-edu-carat-tick="' +
            tick.ct +
            '" tabindex="-1" style="left:' +
            tick.pos * 100 +
            '%" aria-label="Set carat weight to ' +
            escapeHtml(caratAec.formatCaratLabel(tick.ct)) +
            '"><span class="diamond-search__edu-carat-tick-mark" aria-hidden="true"></span><span class="diamond-search__edu-carat-tick-label">' +
            escapeHtml(tickLabel) +
            "</span></button>"
        );
      }
      html.push("</div></div></div>");
      html.push(
        '<p class="diamond-search__edu-scale-explain" id="diamondSearchEduCaratExplain" data-edu-carat-explain aria-live="polite"></p>'
      );
      html.push("</div>");

      moduleHost.innerHTML = html.join("");
      moduleHost.hidden = false;
      panel.classList.add("diamond-search__edu-panel--module");
      activeScaleContent = content;
      preloadCaratToneAssets();
      applyCaratTone(defaultTone);
      applyCaratValue(content, defaultCarat);
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(function () {
          if (activeScaleContent === content) applyCaratValue(content, defaultCarat);
        });
      }
    }

    function applyActiveScaleState(content, stateId) {
      if (isColorScaleContent(content)) applyColorState(content, stateId);
      else if (isClarityScaleContent(content)) applyClarityState(content, stateId);
      else if (isCaratScaleContent(content)) applyCaratState(content, stateId);
      else applyScaleState(content, stateId);
    }

    function onScaleChange(e) {
      var target = e.target;
      if (scaleApplyLock) return;
      if (!activeScaleContent || !target || target.name !== getScaleRadioName(activeScaleContent)) return;
      applyActiveScaleState(activeScaleContent, target.value);
    }

    function onScaleKeydown(e) {
      if (!activeScaleContent || !e.target || e.target.name !== getScaleRadioName(activeScaleContent)) return;
      var states = activeScaleContent.states;
      var next = -1;
      if (e.key === "Home") next = 0;
      else if (e.key === "End") next = states.length - 1;
      if (next < 0) return;
      e.preventDefault();
      applyActiveScaleState(activeScaleContent, states[next].id);
      var radio = moduleHost && moduleHost.querySelector("#" + getScaleRadioName(activeScaleContent) + "-" + states[next].id);
      if (radio) focusNoScroll(radio);
    }

    function closeEdu(options) {
      var restoreFocus = !options || options.restoreFocus !== false;
      var trigger = activeTrigger;
      if (!trigger && shell.hasAttribute("hidden")) return;

      pendingEduContext = null;
      activeTrigger = null;
      setTriggerExpanded(trigger, false);
      panel.setAttribute("aria-modal", "false");
      if (backdrop) backdrop.hidden = true;
      shell.hidden = true;
      clearEduTitle();
      clearScaleModule();
      clearPanelPosition();
      if (restoreFocus) focusNoScroll(trigger);
    }

    function openEdu(trigger) {
      var key = trigger.getAttribute("data-edu-key");
      var content = EDU_CONTENT[key];
      if (!content) return;
      pendingEduContext = readEduContext(trigger);

      if (activeTrigger === trigger) {
        closeEdu();
        return;
      }

      setTriggerExpanded(activeTrigger, false);
      activeTrigger = trigger;
      setEduTitle(content.title);
      setTriggerExpanded(trigger, true);
      clearScaleModule();

      if (isScaleContent(content)) {
        bodyEl.textContent = content.intro || content.body || "";
        try {
          if (isColorScaleContent(content)) renderColorModule(content);
          else if (isClarityScaleContent(content)) renderClarityModule(content);
          else if (isCaratScaleContent(content)) renderCaratModule(content);
          else renderScaleModule(content);
        } catch (err) {
          clearScaleModule();
          bodyEl.textContent = content.intro || content.body || "";
        }
      } else {
        bodyEl.textContent = content.body || "";
      }

      var mobile = isMobile();
      panel.setAttribute("aria-modal", mobile ? "true" : "false");
      if (backdrop) backdrop.hidden = false;
      shell.hidden = false;
      positionPanel();
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(positionPanel);
      }
      focusNoScroll(panel);
    }

    function onViewportChange() {
      if (!activeTrigger) return;
      if (!isMobile() && !triggerIsVisible()) {
        closeEdu({ restoreFocus: false });
        return;
      }
      positionPanel();
      if (isClarityScaleContent(activeScaleContent) && activeClarityStateId) {
        applyClarityState(activeScaleContent, activeClarityStateId);
      }
      if (isCaratScaleContent(activeScaleContent)) {
        applyCaratValue(activeScaleContent, activeCaratValue);
      }
      if (isCutScaleContent(activeScaleContent) && activeCutStateId) {
        applyScaleState(activeScaleContent, activeCutStateId);
      }
      if (isColorScaleContent(activeScaleContent) && activeColorStateId) {
        applyColorState(activeScaleContent, activeColorStateId);
      }
    }

    function getFocusable() {
      var list = [panel];
      var closeBtn = panel.querySelector("[data-edu-dismiss]");
      var caratSlider;
      var toneSlider;
      var cutSlider;
      var colorSlider;
      var claritySlider;
      if (closeBtn) list.push(closeBtn);
      if (moduleHost && !moduleHost.hidden) {
        if (isCaratScaleContent(activeScaleContent)) {
          caratSlider = moduleHost.querySelector("[data-edu-carat-slider]");
          toneSlider = moduleHost.querySelector("[data-edu-carat-tone-slider]");
          if (caratSlider) list.push(caratSlider);
          if (toneSlider && toneSlider.getAttribute("aria-disabled") !== "true") list.push(toneSlider);
        } else if (isCutScaleContent(activeScaleContent)) {
          cutSlider = moduleHost.querySelector("[data-edu-cut-slider]");
          if (cutSlider) list.push(cutSlider);
        } else if (isColorScaleContent(activeScaleContent)) {
          colorSlider = moduleHost.querySelector("[data-edu-color-slider]");
          if (colorSlider) list.push(colorSlider);
        } else if (isClarityScaleContent(activeScaleContent)) {
          claritySlider = moduleHost.querySelector("[data-edu-clarity-slider]");
          if (claritySlider) list.push(claritySlider);
        } else {
          var radioName = getScaleRadioName(activeScaleContent);
          var checked = moduleHost.querySelector('input[name="' + radioName + '"]:checked');
          var fallback = moduleHost.querySelector('input[name="' + radioName + '"]');
          if (checked) list.push(checked);
          else if (fallback) list.push(fallback);
        }
      }
      return list;
    }

    listen(document, "click", function (e) {
      var btn = e.target && e.target.closest ? e.target.closest(".diamond-search__edu-btn[data-edu-key]") : null;
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      openEdu(btn);
    });

    shell.querySelectorAll("[data-edu-dismiss]").forEach(function (el) {
      listen(el, "click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeEdu();
      });
    });

    if (moduleHost) {
      listen(moduleHost, "change", onScaleChange);
      listen(moduleHost, "keydown", onScaleKeydown);
      listen(moduleHost, "keydown", onCaratSliderKeydown);
      listen(moduleHost, "keydown", onCaratToneKeydown);
      listen(moduleHost, "keydown", onCutSliderKeydown);
      listen(moduleHost, "keydown", onColorSliderKeydown);
      listen(moduleHost, "keydown", onClaritySliderKeydown);
      listen(moduleHost, "click", onCaratTickActivate);
      listen(moduleHost, "click", onCutTickActivate);
      listen(moduleHost, "click", onColorTickActivate);
      listen(moduleHost, "click", onClarityTickActivate);
      listen(moduleHost, "pointerdown", onCaratPointerDown);
      listen(moduleHost, "pointerdown", onCaratTonePointerDown);
      listen(moduleHost, "pointerdown", onCutPointerDown);
      listen(moduleHost, "pointerdown", onColorPointerDown);
      listen(moduleHost, "pointerdown", onClarityPointerDown);
    }
    if (window.matchMedia) {
      var toneLayoutMq = window.matchMedia("(max-width: 989px)");
      function resyncCaratToneGeometry() {
        var control = moduleHost && moduleHost.querySelector("[data-edu-carat-tone-slider]");
        if (!control) return;
        applyCaratTone(control.getAttribute("data-edu-carat-tone-value") || getCaratDefaultTone());
      }
      if (toneLayoutMq.addEventListener) toneLayoutMq.addEventListener("change", resyncCaratToneGeometry);
      else if (toneLayoutMq.addListener) toneLayoutMq.addListener(resyncCaratToneGeometry);
    }
    listen(document, "pointermove", onCaratPointerMove);
    listen(document, "pointerup", onCaratPointerUp);
    listen(document, "pointercancel", onCaratPointerUp);
    listen(document, "pointermove", onCutPointerMove);
    listen(document, "pointerup", onCutPointerUp);
    listen(document, "pointercancel", onCutPointerUp);
    listen(document, "pointermove", onColorPointerMove);
    listen(document, "pointerup", onColorPointerUp);
    listen(document, "pointercancel", onColorPointerUp);
    listen(document, "pointermove", onClarityPointerMove);
    listen(document, "pointerup", onClarityPointerUp);
    listen(document, "pointercancel", onClarityPointerUp);

    listen(document, "pointerdown", function (e) {
      if (!activeTrigger) return;
      if (shell.contains(e.target)) return;
      if (e.target.closest && e.target.closest(".diamond-search__edu-btn")) return;
      closeEdu({ restoreFocus: false });
    }, true);

    listen(document, "keydown", function (e) {
      if (!activeTrigger) return;
      if (e.key === "Escape") {
        e.preventDefault();
        closeEdu();
        return;
      }
      if (e.key !== "Tab") return;

      var focusable = getFocusable();
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var current = document.activeElement;

      if (!isMobile() && !shell.contains(current) && current !== activeTrigger) {
        closeEdu({ restoreFocus: false });
        return;
      }

      if (!e.shiftKey && current === last) {
        e.preventDefault();
        focusNoScroll(first);
      } else if (e.shiftKey && (current === first || current === panel)) {
        e.preventDefault();
        focusNoScroll(last);
      }
    });

    if (sidebar) {
      listen(sidebar, "scroll", onViewportChange, { passive: true });
      sidebar.querySelectorAll("details").forEach(function (details) {
        listen(details, "toggle", onViewportChange);
      });
    }
    listen(window, "scroll", onViewportChange, { passive: true });
    listen(window, "resize", onViewportChange);
    if (window.visualViewport) {
      listen(window.visualViewport, "resize", onViewportChange);
    }
    if (mqMobile.addEventListener) {
      listen(mqMobile, "change", onViewportChange);
    } else if (mqMobile.addListener) {
      mqMobile.addListener(onViewportChange);
      cleanups.push(function () {
        if (mqMobile.removeListener) mqMobile.removeListener(onViewportChange);
      });
    }

    return function teardown() {
      try {
        closeEdu({ restoreFocus: false });
      } catch (err) {}
      var i;
      for (i = 0; i < cleanups.length; i++) {
        try {
          cleanups[i]();
        } catch (err) {}
      }
    };
  }

  function dedupeEduShell() {
    var layout = document.getElementById("diamondSearchLayout");
    var fresh = layout && layout.querySelector("#diamondSearchEdu");
    if (!fresh) return;
    var all = document.querySelectorAll("#diamondSearchEdu");
    var i;
    for (i = 0; i < all.length; i++) {
      if (all[i] !== fresh && all[i].parentNode) {
        all[i].parentNode.removeChild(all[i]);
      }
    }
  }

  function boot() {
    if (typeof eduTeardown === "function") {
      try {
        eduTeardown();
      } catch (err) {}
      eduTeardown = null;
    }
    dedupeEduShell();
    eduTeardown = initEducation() || null;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (event) {
    if (!event.target || !event.target.querySelector) return;
    if (
      event.target.id === "diamondSearchEdu" ||
      event.target.id === "diamondSearchLayout" ||
      event.target.querySelector("#diamondSearchEdu") ||
      event.target.querySelector("#diamondSearchLayout") ||
      event.target.hasAttribute("data-diamond-details") ||
      event.target.querySelector("[data-diamond-details]")
    ) {
      boot();
    }
  });
})();
